import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  GoogleGenAI,
  LiveServerMessage,
  Modality,
  StartSensitivity,
  EndSensitivity,
} from "@google/genai";
import { createBlob, decode, decodeAudioData } from "../../tutor/audioUtils";
import { Layout } from "../../components/layout/Layout";
import { useTutorTracking } from "../../tutor/hooks/useTutorTracking";
import { serviceUrls } from "../../config/serviceUrls";
import {
  getStudentProgressMe,
  postCommunicationTurn,
  type CommunicationTurnResponse,
  type PerformanceScores,
  type SkillProgressEntry,
  type StudentProgressResponse,
} from "../../services/api/canDo";

import {
  Mic,
  MicOff,
  PhoneOff,
  PlugZap,
  Unplug,
  RefreshCcw,
  Search,
  BookOpen,
  Tag,
  X,
  MessageCircle,
  Film,
  Loader2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleUser,
  Eye,
  Hand,
  Footprints,
  Heart,
  Laugh,
  Headphones,
  Activity,
  type LucideIcon,
} from "lucide-react";
import { ROUTES } from "../../utils/constants";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import {
  handleSchoolFeatureDisabledIfPresent,
  handleSchoolFeatureDisabledFromAxiosError,
  isSchoolFeatureDisabledHandledError,
  SchoolFeatureDisabledHandledError,
} from "../../utils/schoolFeatureDisabledStudent";
import {
  AuthSessionExpiredHandledError,
  handleTutorSessionTokenAuthExpiredIfPresent,
  isAuthSessionExpiredHandledError,
} from "../../utils/tutorSessionTokenAuth";
import { useStudentMe } from "../../modules/roster/hooks/useStudentMe";
import { ROSTER_STUDENT_ME_QUERY_KEY } from "../../modules/roster/queryKeys";
import { TutorGrade3TextbookPanel } from "./TutorGrade3TextbookPanel";
import {
  getCommunicationInstruction,
  getTutorLessonResume,
  postTutorProgressUpdate,
  postTutorSessionPosition,
  type LessonItem,
  type TutorPageAsset,
  type TutorSublessonItem,
} from "../../modules/tutor/api/tutor.client";
import {
  DEFAULT_STUDENT_TUTOR_DIALECT_KEY,
  pickDialectKeyFromServerList,
} from "../../modules/tutor/defaultStudentDialect";
import { getMySchoolLanguage } from "../../modules/platform/api/schools";
import { isAxiosError } from "axios";
import {
  fetchStories,
  LearningHttpError,
  resolveStoryStudioSelectedIndex,
  storyRowHasPlayableVideo,
} from "../../modules/learning/api/stories";

type TutorType = "ai_tutor" | "communication_tutor";
type StudentProfileStatus = "loading" | "ready" | "error";
type ConnState =
  | "idle"
  | "loadingPrompt"
  | "ready"
  | "connecting"
  | "connected"
  | "error";

/** WebSocket + mic/audio pipeline only — never reset by prompt refetch (`connState` may still reload). */
type LivePhase = "idle" | "connecting" | "connected" | "disconnecting" | "error";

// ✅ Use gateway for all tutor endpoints
const AI_TUTOR_BASE = serviceUrls.gateway;

// Use the 12-2025 version for better Tamil pronunciation
const MODEL = "gemini-2.5-flash-native-audio-preview-09-2025";
// Switch from Firm (Kore) to Clear (Erinome)
const VOICE = "Erinome";

/** Mic capture rate for Live input (must match createBlob mimeType rate=16000). */
const INPUT_AUDIO_CONTEXT_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;
/** ScriptProcessor buffer size (samples); smaller = lower latency, more CPU. */
const SCRIPT_PROCESSOR_BUFFER_SIZE = 2048;
/** Client VAD: RMS below this counts as silence for end-of-turn signal. */
const CLIENT_VAD_SILENCE_RMS = 0.005;
/** Sustained silence before sendRealtimeInput({ audioStreamEnd: true }). */
const CLIENT_VAD_SILENCE_MS = 800;
/** After last streaming model chunk: batch one `communication-turn` POST (avoids dozens of writes per reply). */
const COMMUNICATION_TURN_PERSIST_DEBOUNCE_MS = 550;

/** If UI shows "teacher speaking" but no sources are scheduled, clear after this delay. */
const TEACHER_SPEAKING_WATCHDOG_MS = 900;

/**
 * Body-parts only: trailing silence (ms) before the Live server commits end-of-speech.
 * Lower = teacher answers sooner after the child spells/echoes one word (default tuned for that).
 * Raise via VITE_LIVE_BODY_PARTS_VAD_SILENCE_MS if short words get cut off.
 */
const BODY_PARTS_VAD_SILENCE_MS = Number(
  import.meta.env.VITE_LIVE_BODY_PARTS_VAD_SILENCE_MS ?? 400
);

/** After Live opens: wait this long before the first body-parts teacher nudge (student can speak first). */
const BODY_PARTS_START_GREETING_DELAY_MS = Number(
  import.meta.env.VITE_LIVE_BODY_PARTS_START_DELAY_MS ?? 2000
);

// ✅ Updated to use gateway with /v1 prefix
const LESSONS_ENDPOINT = "/v1/tutor/lesson-index";
const DIALECT_OPTIONS_ENDPOINT = "/v1/tutor/dialect-options";
const AI_TUTOR_PROMPT_ENDPOINT = "/v1/tutor/system-instruction";
const TOKEN_ENDPOINT = "/v1/tutor/session-token";

/** Backend synthetic unit for lesson-only progress when lesson-index has no sublesson TOC rows. */
const LESSON_ONLY_SUBLESSON_NO = "__lesson__";

/** True when lesson-index includes sublesson rows to select (array is source of truth for FE). */
function hasSublessons(lesson: LessonItem | undefined): boolean {
  if (!lesson) return false;
  return (lesson.sublessons?.length ?? 0) > 0;
}

function sortedSublessons(lesson: LessonItem): TutorSublessonItem[] {
  return [...(lesson.sublessons || [])].sort((a, b) => a.sublesson_index - b.sublesson_index);
}

/** Keep preferred sublesson if still valid; else first by sublesson_index; lesson without TOC rows => null unless preferred is lesson-only sentinel. */
function resolveSublessonNoForLesson(lesson: LessonItem | undefined, preferred: string | null): string | null {
  if (!lesson) return null;
  const pref = String(preferred || "").trim();
  if (!hasSublessons(lesson)) {
    // Resume / progress batch use "__lesson__" while lesson-index may omit sublessons entirely.
    if (pref === LESSON_ONLY_SUBLESSON_NO) return LESSON_ONLY_SUBLESSON_NO;
    return null;
  }
  if (pref && lesson.sublessons?.some((s) => s.sublesson_no === pref)) return pref;
  const sorted = sortedSublessons(lesson);
  return sorted[0]?.sublesson_no ?? null;
}

function parsePageAssetsFromApi(raw: unknown): TutorPageAsset[] {
  if (!Array.isArray(raw)) return [];
  const out: TutorPageAsset[] = [];
  for (const x of raw) {
    if (!x || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    const pn = Number(o.page_no);
    const image_url = String(o.image_url ?? "").trim();
    if (!Number.isFinite(pn) || pn < 1 || !image_url) continue;
    const gcs_path = String(o.gcs_path ?? "").trim();
    const typ = String(o.type ?? "").trim();
    out.push({
      page_no: Math.trunc(pn),
      image_url,
      ...(gcs_path ? { gcs_path } : {}),
      ...(typ ? { type: typ } : {}),
    });
  }
  out.sort((a, b) => a.page_no - b.page_no);
  return out;
}

function parseSublessonsFromApi(raw: unknown): TutorSublessonItem[] {
  if (!Array.isArray(raw)) return [];
  const out: TutorSublessonItem[] = [];
  for (const x of raw) {
    if (!x || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    const sublesson_no = String(o.sublesson_no ?? "").trim();
    const title = String(o.title ?? "").trim();
    const idx = Number(o.sublesson_index);
    if (!sublesson_no || !title || !Number.isFinite(idx)) continue;
    const page_assets = parsePageAssetsFromApi(o.page_assets);
    out.push({
      sublesson_no,
      sublesson_index: idx,
      title,
      type: o.type != null ? String(o.type).trim() : undefined,
      page_start: typeof o.page_start === "number" ? o.page_start : undefined,
      page_end: typeof o.page_end === "number" ? o.page_end : undefined,
      ...(page_assets.length > 0 ? { page_assets } : {}),
    });
  }
  return out;
}

/**
 * ✅ IMPORTANT:
 * - TKM stores auth in localStorage: access_token
 * - Tenant stored as: X_TENANT_ID
 * We must forward both to ai-tutor-service.
 */
function getTutorHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token") || "";
  const tenantId = localStorage.getItem("X_TENANT_ID") || "";

  const h: Record<string, string> = {
    Accept: "application/json",
  };

  if (token) h["Authorization"] = `Bearer ${token}`;
  if (tenantId) h["x-tenant-id"] = tenantId;

  return h;
}

async function readErrText(r: Response) {
  const txt = await r.text().catch(() => "");
  return txt || r.statusText || "Request failed";
}

function normalizeLessonType(t?: string) {
  const lt = String(t || "").trim().toLowerCase();
  if (!lt) return "";
  if (lt === "rhymes") return "Rhymes";
  if (lt === "story") return "Story";
  if (lt === "pesuvom") return "Pesuvom";
  if (lt === "body_parts") return "Body parts";
  return lt.charAt(0).toUpperCase() + lt.slice(1);
}

/** Snake_case key for matching API / Mongo lesson and sublesson `type` strings. */
function normalizeLessonTypeKey(raw?: string | null): string {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

/** Types that open Story Studio (rhyme, story, or legacy names). */
const STORY_STUDIO_MEDIA_TYPES = new Set([
  "story",
  "short_story_passage",
  "poetry_rhyme",
  "story_listening",
  "rhymes",
]);

export type BodyPartItem = { id: string; label_ta: string; image_url?: string };

/** When RAG `type` is still "lesson" but the unit is clearly body parts (e.g. textbook title). */
function isBodyPartsLessonByTitle(title: string): boolean {
  const t = String(title || "").trim();
  return t.includes("உடல்") && t.includes("உறுப்பு");
}

/** Mirrors backend defaults if API omits `body_parts`. */
const FALLBACK_BODY_PARTS: BodyPartItem[] = [
  { id: "head", label_ta: "தலை", image_url: "/images/body-parts/head.png" },
  { id: "eye", label_ta: "கண்", image_url: "/images/body-parts/eye.png" },
  { id: "ear", label_ta: "காது", image_url: "/images/body-parts/ear.png" },
  { id: "nose", label_ta: "மூக்கு", image_url: "/images/body-parts/nose.png" },
  { id: "mouth", label_ta: "வாய்", image_url: "/images/body-parts/mouth.png" },
  { id: "hand", label_ta: "கை", image_url: "/images/body-parts/hand.png" },
  { id: "leg", label_ta: "கால்", image_url: "/images/body-parts/leg.png" },
  { id: "foot", label_ta: "கால் / பாதம்", image_url: "/images/body-parts/foot.png" },
  { id: "stomach", label_ta: "வயிறு", image_url: "/images/body-parts/stomach.png" },
];

const BODY_PART_ICON: Record<string, LucideIcon> = {
  head: CircleUser,
  eye: Eye,
  ear: Headphones,
  nose: Activity,
  mouth: Laugh,
  hand: Hand,
  leg: Footprints,
  foot: Footprints,
  stomach: Heart,
};

function pickSendClientContent(session: unknown): ((text: string) => Promise<void>) | null {
  const s = session as Record<string, unknown> | null;
  if (!s) return null;
  if (typeof s.sendClientContent === "function") {
    return async (text: string) =>
      (s.sendClientContent as (x: unknown) => Promise<void>)({
        turns: [{ role: "user", parts: [{ text }] }],
        turnComplete: true,
      });
  }
  if (typeof s.send === "function") {
    return async (text: string) =>
      (s.send as (x: unknown) => Promise<void>)({
        clientContent: {
          turns: [{ role: "user", parts: [{ text }] }],
          turnComplete: true,
        },
      });
  }
  return null;
}

function BodyPartCard({
  part,
  disabled,
  active = false,
  onSelect,
}: {
  part: BodyPartItem;
  disabled: boolean;
  active?: boolean;
  onSelect: () => void;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const Icon = BODY_PART_ICON[part.id] || CircleUser;
  const src = part.image_url?.trim();

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={`group rounded-2xl border bg-white p-3 shadow-sm transition text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 cursor-pointer ${
        active
          ? "border-indigo-400 ring-2 ring-indigo-200 shadow-md"
          : "border-slate-200 hover:border-indigo-300 hover:shadow-md"
      } disabled:opacity-60 disabled:cursor-not-allowed`}
      aria-pressed={active}
    >
      <div className="aspect-square rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden mb-2">
        {src && !imgFailed ? (
          <img
            src={src}
            alt={part.label_ta ? `${part.label_ta} — body part picture` : `Body part: ${part.id}`}
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <Icon className="w-12 h-12 text-indigo-400 group-hover:text-indigo-600 transition" size={48} />
        )}
      </div>
      <p className="text-sm font-extrabold text-emerald-800 text-center leading-tight">{part.label_ta}</p>
    </button>
  );
}

/** Communication topic row; `topic_id` is required for the main tutor instruction request. */
export type TopicItem = {
  topic_id: string;
  title: string;
  description?: string;
  grade?: string;
  section_id?: string;
  /** When present, sent as `practice_can_do_id` on communication turn posts. */
  practiceCanDoId?: string;
  /** Optional legacy id (e.g. same as topic or a can-do key) for turn payloads when `practiceCanDoId` is unset. */
  id?: string;
};

function meanPerformanceScore(p: PerformanceScores): number {
  return (p.fluency + p.accuracy + p.appropriateness) / 3;
}

/** Buckets can-dos by average rubric score for the Communication Tutor skill progress panel. */
function CommunicationSkillProgressBuckets({ skills }: { skills: SkillProgressEntry[] }) {
  const done = skills.filter((s) => meanPerformanceScore(s.avg_performance) >= 0.7);
  const mid = skills.filter((s) => {
    const m = meanPerformanceScore(s.avg_performance);
    return m >= 0.4 && m < 0.7;
  });
  const low = skills.filter((s) => meanPerformanceScore(s.avg_performance) < 0.4);
  const block = (title: string, icon: string, items: SkillProgressEntry[]) =>
    items.length ? (
      <div>
        <p className="font-bold text-slate-700">
          {icon} {title}
        </p>
        <ul className="mt-1 space-y-1 text-slate-600">
          {items.map((s) => (
            <li key={s.can_do_id} className="line-clamp-2">
              {s.statement}
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  return (
    <>
      {block("Completed", "✅", done)}
      {block("Improving", "⚠️", mid)}
      {block("Needs practice", "❌", low)}
    </>
  );
}

export default function TeacherLive({
  onEnd,
  tutorType,
  initialTopicLabel,
  initialTopicId,
  initialLessonNo,
  initialSublessonNo,
  initialChunkOrderIndex,
  onSessionStart,
  showLessonsPanel = true,
  showTopicsPanel = false,
  topics = [],
  topicsLoading = false,
  /** Lesson number for Communication Tutor can-do tagging (URL query default 1). */
  communicationLessonNo = 0,
  onCommunicationTurnComplete,
  hideSidebar = false,
}: {
  onEnd: () => void;
  tutorType: TutorType;
  initialTopicLabel?: string;
  initialTopicId?: string;
  initialLessonNo?: number;
  /** Deep-link from dashboard resume */
  initialSublessonNo?: string;
  initialChunkOrderIndex?: number;
  onSessionStart?: () => void;
  showLessonsPanel?: boolean;
  showTopicsPanel?: boolean;
  topics?: TopicItem[];
  topicsLoading?: boolean;
  communicationLessonNo?: number;
  /** After each successful Communication Tutor turn POST. */
  onCommunicationTurnComplete?: (res: CommunicationTurnResponse) => void;
  /** When true, hide the app left sidebar (e.g. LMS student launch). */
  hideSidebar?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetch: refetchFeatureAccess } = useFeatureAccess();
  const { beginSession, recordTurn, endSession, getSessionId } = useTutorTracking();
  const lastUserTextRef = useRef("");
  /** Latest model text in the current Live turn (streaming sends cumulative or chunked text). */
  const commModelTextDraftRef = useRef("");
  /** True if any model audio chunk arrived this turn (voice-only replies). */
  const commModelHadAudioRef = useRef(false);
  /** Prevents overlapping POST /communication-turn while a prior request is in flight. */
  const communicationTurnInFlightRef = useRef(false);
  /** Student line for debounced persist path; model snippets in `commModelPiecesRef`. */
  const commPairStudentRef = useRef("");
  const commModelPiecesRef = useRef<string[]>([]);
  const commPersistTimerRef = useRef<number | null>(null);
  const [lastTurnInsight, setLastTurnInsight] = useState<CommunicationTurnResponse | null>(null);
  const [skillProgressOpen, setSkillProgressOpen] = useState(false);
  const [skillProgress, setSkillProgress] = useState<StudentProgressResponse | null>(null);
  const [skillProgressLoading, setSkillProgressLoading] = useState(false);
  const skillProgressLoadedRef = useRef(false);
  const {
    grade,
    isRosterLoading,
    isSuccess: rosterFetchSuccess,
    isError: rosterFetchError,
    error: rosterQueryError,
  } = useStudentMe();
  const [lessonNo, setLessonNo] = useState<number | null>(initialLessonNo ?? null);
  const [selectedSublessonNo, setSelectedSublessonNo] = useState<string | null>(null);
  /** When set, prompt includes chunk_order_index (resume / explicit segment). */
  const [chunkOrderIndex, setChunkOrderIndex] = useState<number | null>(() =>
    initialChunkOrderIndex != null && initialChunkOrderIndex >= 0 ? initialChunkOrderIndex : null
  );
  const [expandedLessonNos, setExpandedLessonNos] = useState<Record<number, boolean>>({});
  const [dialectKey, setDialectKey] = useState<string>(DEFAULT_STUDENT_TUTOR_DIALECT_KEY);
  const [dialectOptions, setDialectOptions] = useState<{ key: string; name: string }[]>([]);

  const [lessonTitle, setLessonTitle] = useState<string>("");
  const [lessonStyle, setLessonStyle] = useState<string>("");
  const [lessonType, setLessonType] = useState<string>(""); // ✅ NEW
  const [bodyParts, setBodyParts] = useState<BodyPartItem[]>([]);
  const [activeBodyPartId, setActiveBodyPartId] = useState<string | null>(null);
  const [systemInstruction, setSystemInstruction] = useState<string>("");

  const [connState, setConnState] = useState<ConnState>("idle");
  /** Live Gemini session lifecycle — single source of truth for Start/Disconnect, mic, avatar "online". */
  const [livePhase, setLivePhase] = useState<LivePhase>("idle");
  const livePhaseRef = useRef<LivePhase>(livePhase);
  useEffect(() => {
    livePhaseRef.current = livePhase;
  }, [livePhase]);
  const [error, setError] = useState<string | null>(null);

  const [isMuted, setIsMuted] = useState(true);
  const [isTeacherSpeaking, setIsTeacherSpeaking] = useState(false);
  const [userIsSpeaking, setUserIsSpeaking] = useState(false);

  const [lessons, setLessons] = useState<LessonItem[]>([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [lessonsError, setLessonsError] = useState<string | null>(null);
  /** True after first lesson-index fetch completes (success or error). Keeps TOC from showing "No lessons found." before load. */
  const [lessonsLoadedOnce, setLessonsLoadedOnce] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<TopicItem | null>(null);
  const [topicSearch, setTopicSearch] = useState("");
  /** Grade 3 textbook multi-page navigation (index into `grade3TextbookAssets`). */
  const [g3BookPageIx, setG3BookPageIx] = useState(0);
  /** Prior communication topic id — disconnect Live when it changes while connected */
  const prevCommTopicIdRef = useRef<string | null>(null);

  const TOTAL_TIME = 120;
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const timerRef = useRef<number | null>(null);
  const hasTimerStartedRef = useRef(false);

  /** ✅ NEW: popup state */
  const [showEndPopup, setShowEndPopup] = useState(false);

  /** Mobile TOC drawer */
  const [tocOpen, setTocOpen] = useState(false);
  /** Mobile topics drawer */
  const [topicsDrawerOpen, setTopicsDrawerOpen] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const sessionPromiseRef = useRef<Promise<any> | null>(null);
  const liveSessionRef = useRef<unknown>(null);
  const nextStartTimeRef = useRef(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const teacherSpeakingWatchdogRef = useRef<number | null>(null);

  const isMutedRef = useRef(isMuted);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const isTeacherSpeakingRef = useRef(isTeacherSpeaking);
  useEffect(() => {
    isTeacherSpeakingRef.current = isTeacherSpeaking;
  }, [isTeacherSpeaking]);

  /** Accumulated silence duration (ms) for client VAD while mic unmuted. */
  const clientSilenceMsRef = useRef(0);
  /** True after we sent audioStreamEnd until RMS rises above silence threshold again. */
  const clientVadEndSentRef = useRef(false);

  const connectLockRef = useRef(false);
  const didOpenRef = useRef(false);
  /** True while closing the Live WebSocket from disconnectSession; avoids spurious onclose error after didOpenRef is cleared. */
  const intentionalDisconnectRef = useRef(false);
  const bodyPartsGreetingTimerRef = useRef<number | null>(null);
  /** Ensures `finalizeLiveSocketUnexpectedEnd` runs at most once per socket death (SDK may emit close + error). */
  const liveUnexpectedTeardownLockRef = useRef(false);
  /** Incremented on each connect attempt so stale onmessage/mic handlers from prior sessions are ignored. */
  const liveConnectGenerationRef = useRef(0);
  /** Tracks lesson/sublesson for mid-session change disconnect (skip initial mount). */
  const connectedLessonKeyRef = useRef<{ lessonNo: number | null; sublessonNo: string | null } | null>(
    null
  );

  const isConnected = livePhase === "connected";

  /** Fresh values for progress interval + async handlers (avoid stale closures). */
  const lessonNoForProgressRef = useRef<number | null>(null);
  const gradeForProgressRef = useRef<number | null>(null);
  const selectedSublessonNoForProgressRef = useRef<string | null>(null);
  const chunkOrderIndexForProgressRef = useRef<number | null>(null);
  useEffect(() => {
    lessonNoForProgressRef.current = lessonNo;
    gradeForProgressRef.current = grade ?? null;
    selectedSublessonNoForProgressRef.current = selectedSublessonNo;
    chunkOrderIndexForProgressRef.current = chunkOrderIndex;
  }, [lessonNo, grade, selectedSublessonNo, chunkOrderIndex]);

  /** When the server marks a segment COMPLETED, move resume cursor to the next segment. */
  const handleProgressApiResult = useCallback((data: { status?: string } | undefined) => {
    if (!data || data.status !== "COMPLETED") return;
    const ln = lessonNoForProgressRef.current;
    const g = gradeForProgressRef.current;
    const sub = selectedSublessonNoForProgressRef.current;
    const cur = chunkOrderIndexForProgressRef.current;
    if (ln == null || g == null || !sub || cur == null) return;
    const next = cur + 1;
    void postTutorSessionPosition(
      { lesson_no: ln, sublesson_no: sub, chunk_order_index: next },
      g
    )
      .then(() => {
        chunkOrderIndexForProgressRef.current = next;
        setChunkOrderIndex(next);
      })
      .catch((e: unknown) => {
        /* Non-fatal: bad chunk index / server validation — never disconnect live UI. */
        if (import.meta.env.DEV) {
          console.warn("[TeacherLive] session/update failed after COMPLETED (non-fatal)", {
            lesson_no: ln,
            sublesson_no: sub,
            chunk_order_index: next,
            err: e,
          });
        }
      });
  }, []);

  const clearCommPersistTimer = useCallback(() => {
    if (commPersistTimerRef.current != null) {
      window.clearTimeout(commPersistTimerRef.current);
      commPersistTimerRef.current = null;
    }
  }, []);

  const flushCommunicationPersist = useCallback(() => {
    clearCommPersistTimer();
    const pieces = commModelPiecesRef.current;
    if (!pieces.length) return;
    if (tutorType !== "communication_tutor" || grade == null || communicationLessonNo < 0) {
      commModelPiecesRef.current = [];
      return;
    }
    const studentMsg = commPairStudentRef.current.trim() || "(no transcript)";
    const aiJoined =
      pieces
        .map((p) => p.trim())
        .filter(Boolean)
        .join("\n")
        .trim() || "(no transcript)";
    commModelPiecesRef.current = [];
    void postCommunicationTurn({
      student_message: studentMsg,
      ai_response: aiJoined,
      grade,
      lesson_no: communicationLessonNo,
      session_id: getSessionId() ?? undefined,
      practice_can_do_id: selectedTopic?.practiceCanDoId ?? selectedTopic?.id ?? undefined,
    })
      .then((res) => {
        setLastTurnInsight(res);
        void getStudentProgressMe().then(setSkillProgress).catch(() => {});
      })
      .catch(() => {});
  }, [
    clearCommPersistTimer,
    tutorType,
    grade,
    communicationLessonNo,
    getSessionId,
    selectedTopic?.practiceCanDoId,
    selectedTopic?.id,
  ]);

  const scheduleCommunicationPersistModelSnippet = useCallback(
    (snippet: string) => {
      const t = snippet.trim();
      if (!t) return;
      if (tutorType !== "communication_tutor" || grade == null || communicationLessonNo < 0) return;
      const buf = commModelPiecesRef.current;
      const last = buf[buf.length - 1];
      if (t === "(audio response)" && last === "(audio response)") {
        /* Gemini emits many audio chunks per utterance; keep a single placeholder until flush */
      } else {
        buf.push(t);
      }
      clearCommPersistTimer();
      commPersistTimerRef.current = window.setTimeout(() => {
        commPersistTimerRef.current = null;
        flushCommunicationPersist();
      }, COMMUNICATION_TURN_PERSIST_DEBOUNCE_MS);
    },
    [
      tutorType,
      grade,
      communicationLessonNo,
      clearCommPersistTimer,
      flushCommunicationPersist,
    ]
  );

  const recordTurnWithProgress = useCallback(
    (role: "user" | "model", text: string) => {
      const trimmed = text.trim();
      if (role === "user" && trimmed) {
        if (
          tutorType === "communication_tutor" &&
          grade != null &&
          communicationLessonNo >= 0
        ) {
          clearCommPersistTimer();
          flushCommunicationPersist();
          commPairStudentRef.current = trimmed;
        }
        lastUserTextRef.current = trimmed;
      }
      recordTurn(role, text);
      // Communication POST /communication-turn is deferred to model turnComplete (see flushCommunicationTurnFromLive)
      // so streaming audio chunks do not each trigger a server write.
      if (
        role !== "user" ||
        tutorType !== "ai_tutor" ||
        lessonNo == null ||
        grade == null ||
        !selectedSublessonNo ||
        chunkOrderIndex == null
      ) {
        return;
      }
      void postTutorProgressUpdate(
        {
          lesson_no: lessonNo,
          sublesson_no: selectedSublessonNo,
          chunk_order_index: chunkOrderIndex,
          interaction_done: true,
          time_spent_sec: 0,
        },
        grade
      )
        .then((data) => handleProgressApiResult(data))
        .catch(() => {});
    },
    [
      recordTurn,
      tutorType,
      lessonNo,
      grade,
      selectedSublessonNo,
      chunkOrderIndex,
      handleProgressApiResult,
    ]
  );

  const flushCommunicationTurnFromLive = useCallback(
    (reason: "turn_complete") => {
      if (tutorType !== "communication_tutor" || grade == null || communicationLessonNo < 0) return;
      if (communicationTurnInFlightRef.current) return;

      const userLine = lastUserTextRef.current.trim();
      const modelText =
        commModelTextDraftRef.current.trim() ||
        (commModelHadAudioRef.current ? "(audio response)" : "");
      if (!userLine && !modelText) {
        commModelTextDraftRef.current = "";
        commModelHadAudioRef.current = false;
        return;
      }

      communicationTurnInFlightRef.current = true;
      if (import.meta.env.DEV) {
        console.debug(`[TeacherLive] communication-turn fired because: ${reason}`);
      }

      void postCommunicationTurn({
        student_message: userLine || "(no transcript)",
        ai_response: modelText || "(audio response)",
        grade,
        lesson_no: communicationLessonNo,
        session_id: getSessionId() ?? undefined,
        practice_can_do_id: selectedTopic?.practiceCanDoId ?? selectedTopic?.id ?? undefined,
      })
        .then((res) => {
          setLastTurnInsight(res);
          onCommunicationTurnComplete?.(res);
          void getStudentProgressMe()
            .then(setSkillProgress)
            .catch(() => {});
        })
        .catch(() => {})
        .finally(() => {
          communicationTurnInFlightRef.current = false;
        });

      if (!commModelTextDraftRef.current.trim() && commModelHadAudioRef.current) {
        recordTurn("model", "(audio response)");
      }

      commModelTextDraftRef.current = "";
      commModelHadAudioRef.current = false;
    },
    [
      tutorType,
      grade,
      communicationLessonNo,
      getSessionId,
      selectedTopic?.id,
      selectedTopic?.practiceCanDoId,
      onCommunicationTurnComplete,
      recordTurn,
    ]
  );

  useEffect(() => {
    if (!skillProgressOpen || tutorType !== "communication_tutor") return;
    if (skillProgressLoadedRef.current) return;
    skillProgressLoadedRef.current = true;
    setSkillProgressLoading(true);
    getStudentProgressMe()
      .then(setSkillProgress)
      .catch(() => {})
      .finally(() => setSkillProgressLoading(false));
  }, [skillProgressOpen, tutorType]);

  const studentProfileStatus = useMemo((): StudentProfileStatus => {
    if (isRosterLoading) return "loading";
    if (rosterFetchSuccess && grade != null) return "ready";
    return "error";
  }, [isRosterLoading, rosterFetchSuccess, grade]);

  const studentProfileError =
    !isRosterLoading && (rosterFetchError || (rosterFetchSuccess && grade == null))
      ? "Could not determine student grade."
      : null;

  const profileReady = studentProfileStatus === "ready" && grade != null;
  // Communication tutor: progress panel (extracted so we can render it as a side card)
  const communicationProgressPanel =
    tutorType === "communication_tutor" && profileReady ? (
      <div className="w-full lg:w-[360px] xl:w-[420px] shrink-0 space-y-3">
        {lastTurnInsight && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 text-sm">
            <p className="text-[10px] font-extrabold text-indigo-900 uppercase tracking-wide">Skill feedback</p>
            <p className="mt-1 text-slate-800">
              You practiced:{" "}
              <span className="font-semibold text-indigo-900">
                {lastTurnInsight.statements_for_ui[0]?.statement_en ||
                  lastTurnInsight.statements_for_ui[0]?.statement_ta ||
                  lastTurnInsight.can_do.auto_ids[0] ||
                  "—"}
              </span>
            </p>
            <div className="mt-2 space-y-1.5">
              {(
                [
                  ["Fluency", lastTurnInsight.performance.fluency],
                  ["Accuracy", lastTurnInsight.performance.accuracy],
                  ["Appropriateness", lastTurnInsight.performance.appropriateness],
                ] as const
              ).map(([label, v]) => (
                <div key={label}>
                  <div className="flex justify-between text-[10px] text-slate-600">
                    <span>{label}</span>
                    <span>{Math.round(v * 100)}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 rounded-full transition-all"
                      style={{ width: `${Math.round(v * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-slate-50/80 overflow-hidden">
          <button
            type="button"
            onClick={() => setSkillProgressOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-left text-xs font-bold text-slate-800 hover:bg-slate-100/80"
          >
            <span>Skill progress</span>
            <ChevronDown
              size={16}
              className={`text-slate-500 transition-transform ${skillProgressOpen ? "rotate-180" : ""}`}
            />
          </button>
          {skillProgressOpen && (
            <div className="px-3 pb-3 text-xs border-t border-slate-200 bg-white">
              {skillProgressLoading && (
                <div className="py-3 flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </div>
              )}
              {!skillProgressLoading && skillProgress && skillProgress.skills.length === 0 && (
                <p className="py-2 text-slate-500">Practice more to see skill trends.</p>
              )}
              {!skillProgressLoading && skillProgress && skillProgress.skills.length > 0 && (
                <div className="space-y-3 pt-2">
                  {(() => {
                    const mean = (p: { fluency: number; accuracy: number; appropriateness: number }) =>
                      (p.fluency + p.accuracy + p.appropriateness) / 3;
                    const done = skillProgress.skills.filter((s) => mean(s.avg_performance) >= 0.7);
                    const mid = skillProgress.skills.filter((s) => {
                      const m = mean(s.avg_performance);
                      return m >= 0.4 && m < 0.7;
                    });
                    const low = skillProgress.skills.filter((s) => mean(s.avg_performance) < 0.4);
                    const block = (title: string, icon: string, items: typeof skillProgress.skills) =>
                      items.length ? (
                        <div>
                          <p className="font-bold text-slate-700">
                            {icon} {title}
                          </p>
                          <ul className="mt-1 space-y-1 text-slate-600">
                            {items.map((s) => (
                              <li key={s.can_do_id} className="line-clamp-2">
                                {s.statement}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null;
                    return (
                      <>
                        {block("Completed", "✅", done)}
                        {block("Improving", "⚠️", mid)}
                        {block("Needs practice", "❌", low)}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    ) : null;
  const busy =
    connState === "connecting" ||
    connState === "loadingPrompt" ||
    studentProfileStatus === "loading" ||
    livePhase === "connecting" ||
    livePhase === "disconnecting";
  const canConnect =
    profileReady && (connState === "ready" || connState === "error");
  /** Start Class button: immediate feedback while prompt or Live handshake is in progress. */
  const isStarting =
    livePhase === "connecting" ||
    connState === "connecting" ||
    connState === "loadingPrompt";
  const showToc = tutorType === "ai_tutor" && showLessonsPanel;
  const showTopics = tutorType === "communication_tutor" && showTopicsPanel;
  const commTopicsStillLoading = tutorType === "communication_tutor" && topicsLoading;
  const liveStatusConnState =
    commTopicsStillLoading && connState === "error" ? "idle" : connState;

  // ✅ derive lessonType from TOC list as fallback
  const lessonTypeFromToc = useMemo(() => {
    const found = lessons.find((l) => l.lesson_no === lessonNo);
    return found?.lesson_type || "";
  }, [lessons, lessonNo]);

  const lessonTitleFromToc = useMemo(() => {
    const found = lessons.find((l) => l.lesson_no === lessonNo);
    return String(found?.lesson_title || "").trim();
  }, [lessons, lessonNo]);

  const currentLesson = useMemo(
    () => (lessonNo != null ? lessons.find((l) => l.lesson_no === lessonNo) : undefined),
    [lessons, lessonNo]
  );

  const currentLessonHasSublessons = useMemo(
    () => !!currentLesson && hasSublessons(currentLesson),
    [currentLesson]
  );

  const selectedSublessonMeta = useMemo(() => {
    if (!currentLesson || !selectedSublessonNo) return null;
    const s = currentLesson.sublessons?.find((x) => x.sublesson_no === selectedSublessonNo);
    return s ? { title: s.title, type: s.type } : null;
  }, [currentLesson, selectedSublessonNo]);

  const grade3TextbookAssets = useMemo((): TutorPageAsset[] => {
    if (tutorType !== "ai_tutor" || (grade !== 2 && grade !== 3) || !currentLesson) return [];
    if (selectedSublessonNo && (currentLesson.sublessons?.length ?? 0) > 0) {
      const sub = currentLesson.sublessons?.find((s) => s.sublesson_no === selectedSublessonNo);
      const own = sub?.page_assets;
      if (own && own.length > 0) return own;
    }
    return currentLesson.page_assets ?? [];
  }, [tutorType, grade, currentLesson, selectedSublessonNo]);

  const g3AssetsNavKey = useMemo(
    () => grade3TextbookAssets.map((a) => `${a.page_no}:${a.image_url}`).join("|"),
    [grade3TextbookAssets]
  );

  useEffect(() => {
    setG3BookPageIx(0);
  }, [lessonNo, selectedSublessonNo, g3AssetsNavKey]);

  const showGrade3TextbookPanel =
    tutorType === "ai_tutor" && (grade === 2 || grade === 3) && grade3TextbookAssets.length > 0;

  const effectiveLessonType = tutorType === "ai_tutor" ? (lessonType || lessonTypeFromToc) : "";

  const normalizedLessonType = String(effectiveLessonType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const activeSublessonTypeKey = useMemo(
    () => normalizeLessonTypeKey(selectedSublessonMeta?.type),
    [selectedSublessonMeta?.type]
  );

  /** Rhyme/story (etc.) segment type — button only appears if learning-service has a video for this slot. */
  const storyStudioTypeEligible = useMemo(() => {
    if (tutorType !== "ai_tutor" || lessonNo == null || lessonNo < 1) return false;

    if (currentLessonHasSublessons) {
      // Only for the sublesson the user selected (rhyme / story segments), not the whole lesson.
      if (!selectedSublessonNo || !activeSublessonTypeKey) return false;
      return STORY_STUDIO_MEDIA_TYPES.has(activeSublessonTypeKey);
    }

    // No sublesson TOC: lesson-level type only (e.g. Grade 2 `story`).
    return STORY_STUDIO_MEDIA_TYPES.has(normalizedLessonType);
  }, [
    tutorType,
    lessonNo,
    currentLessonHasSublessons,
    selectedSublessonNo,
    activeSublessonTypeKey,
    normalizedLessonType,
  ]);

  /** Query param for Story Studio auto-select (rhyme vs story). */
  const storyStudioSegmentParam = useMemo(() => {
    const typeForLabel = currentLessonHasSublessons ? activeSublessonTypeKey : normalizedLessonType;
    if (typeForLabel === "poetry_rhyme" || typeForLabel === "rhymes") return "rhyme";
    if (
      typeForLabel === "story" ||
      typeForLabel === "short_story_passage" ||
      typeForLabel === "story_listening"
    ) {
      return "story";
    }
    return "";
  }, [currentLessonHasSublessons, activeSublessonTypeKey, normalizedLessonType]);

  const [storyStudioHasVideo, setStoryStudioHasVideo] = useState<boolean | null>(null);

  useEffect(() => {
    if (
      tutorType !== "ai_tutor" ||
      !storyStudioTypeEligible ||
      grade == null ||
      lessonNo == null ||
      lessonNo < 1
    ) {
      setStoryStudioHasVideo(null);
      return;
    }
    let cancelled = false;
    setStoryStudioHasVideo(null);
    const bookId =
      typeof currentLesson?.book_id === "string" && currentLesson.book_id.trim() !== ""
        ? currentLesson.book_id.trim()
        : undefined;
    const subForApi = currentLessonHasSublessons ? (selectedSublessonNo ?? "").trim() : "";
    void fetchStories(grade, { bookId, lessonNo })
      .then((list) => {
        if (cancelled) return;
        const idx = resolveStoryStudioSelectedIndex(list, {
          sidFromUrl: null,
          lessonFromUrl: String(lessonNo),
          sublessonFromUrl: subForApi,
          segmentFromUrl: storyStudioSegmentParam,
        });
        const row = list[idx];
        setStoryStudioHasVideo(storyRowHasPlayableVideo(row));
      })
      .catch(async (e) => {
        if (cancelled) return;
        if (e instanceof LearningHttpError) {
          const handled = await handleSchoolFeatureDisabledIfPresent({
            status: e.status,
            bodyText: e.bodyText,
            navigate,
            refetchFeatureAccess,
            fallbackFeatureKey: "story_studio",
          });
          if (handled) {
            setStoryStudioHasVideo(false);
            return;
          }
        }
        setStoryStudioHasVideo(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    tutorType,
    storyStudioTypeEligible,
    grade,
    lessonNo,
    currentLesson?.book_id,
    currentLessonHasSublessons,
    selectedSublessonNo,
    storyStudioSegmentParam,
    navigate,
    refetchFeatureAccess,
  ]);

  const showStoryStudioButton = useMemo(
    () => Boolean(storyStudioTypeEligible && storyStudioHasVideo === true),
    [storyStudioTypeEligible, storyStudioHasVideo]
  );

  const storyStudioWatchLabel = useMemo(() => {
    const typeForLabel = currentLessonHasSublessons ? activeSublessonTypeKey : normalizedLessonType;
    if (typeForLabel === "poetry_rhyme" || typeForLabel === "rhymes") return "Watch rhyme video";
    if (
      typeForLabel === "story" ||
      typeForLabel === "short_story_passage" ||
      typeForLabel === "story_listening"
    ) {
      return "Watch story video";
    }
    return "Watch lesson video";
  }, [currentLessonHasSublessons, activeSublessonTypeKey, normalizedLessonType]);

  /** `body_parts` from API, or title heuristic when Mongo type is still `lesson`. */
  const isBodyPartsLesson =
    tutorType === "ai_tutor" &&
    (normalizedLessonType === "body_parts" ||
      isBodyPartsLessonByTitle(lessonTitle) ||
      isBodyPartsLessonByTitle(lessonTitleFromToc));

  const bodyPartsForCards = useMemo(() => {
    if (!isBodyPartsLesson) return [];
    if (bodyParts.length > 0) return bodyParts;
    return FALLBACK_BODY_PARTS;
  }, [isBodyPartsLesson, bodyParts]);

  // Roster /me: school-feature 403 redirects here (queryFn stays pure)
  useEffect(() => {
    if (!rosterFetchError || !rosterQueryError) return;
    let cancelled = false;
    (async () => {
      const handled = await handleSchoolFeatureDisabledFromAxiosError(
        rosterQueryError,
        navigate,
        refetchFeatureAccess,
        tutorType
      );
      if (!cancelled && handled) {
        queryClient.removeQueries({ queryKey: ROSTER_STUDENT_ME_QUERY_KEY });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rosterFetchError, rosterQueryError, navigate, refetchFeatureAccess, tutorType, queryClient]);

  useEffect(() => {
    if (isRosterLoading) return;
    if (rosterFetchError || (rosterFetchSuccess && grade == null)) {
      setLessonsLoadedOnce(true);
    }
  }, [isRosterLoading, rosterFetchError, rosterFetchSuccess, grade]);

  // Keep sublesson selection valid when lesson list or active lesson changes (lesson-index is source of truth)
  useEffect(() => {
    if (tutorType !== "ai_tutor" || lessonNo == null || lessons.length === 0) return;
    const cur = lessons.find((l) => l.lesson_no === lessonNo);
    if (!cur) return;
    setSelectedSublessonNo((prev) => resolveSublessonNoForLesson(cur, prev));
    if (hasSublessons(cur)) {
      setExpandedLessonNos((e) => {
        if (e[lessonNo] !== undefined) return e;
        return { ...e, [lessonNo]: true };
      });
    }
  }, [lessons, lessonNo, tutorType]);

  useEffect(() => {
    const s = initialSublessonNo?.trim();
    if (s) setSelectedSublessonNo(s);
  }, [initialSublessonNo]);

  useEffect(() => {
    if (initialChunkOrderIndex != null && initialChunkOrderIndex >= 0) {
      setChunkOrderIndex(initialChunkOrderIndex);
    }
  }, [initialChunkOrderIndex]);

  useEffect(() => {
    if (tutorType !== "ai_tutor" || lessonNo == null || grade == null || !lessonsLoadedOnce) return;
    if (initialChunkOrderIndex != null) return;
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await getTutorLessonResume(lessonNo, grade);
        if (ac.signal.aborted) return;
        if (!initialSublessonNo?.trim() && r?.sublesson_no) {
          setSelectedSublessonNo((prev) => prev || r.sublesson_no);
        }
        if (r?.from_saved_session && typeof r.chunk_order_index === "number") {
          setChunkOrderIndex(r.chunk_order_index);
        }
      } catch {
        /* best-effort */
      }
    })();
    return () => ac.abort();
  }, [
    tutorType,
    lessonNo,
    grade,
    lessonsLoadedOnce,
    initialChunkOrderIndex,
    initialSublessonNo,
  ]);

  // Segment index 0 whenever a sublesson is active but URL/resume did not set a chunk yet.
  useEffect(() => {
    if (tutorType !== "ai_tutor" || !selectedSublessonNo || lessonNo == null) return;
    setChunkOrderIndex((prev) => (prev === null ? 0 : prev));
  }, [tutorType, lessonNo, selectedSublessonNo]);

  useEffect(() => {
    if (
      tutorType !== "ai_tutor" ||
      !isConnected ||
      lessonNo == null ||
      grade == null ||
      !selectedSublessonNo ||
      chunkOrderIndex == null
    ) {
      return;
    }
    const tickSec = 8;
    const id = window.setInterval(() => {
      const ln = lessonNoForProgressRef.current;
      const g = gradeForProgressRef.current;
      const sub = selectedSublessonNoForProgressRef.current;
      const ci = chunkOrderIndexForProgressRef.current;
      if (ln == null || g == null || !sub || ci == null) return;
      void postTutorProgressUpdate(
        {
          lesson_no: ln,
          sublesson_no: sub,
          chunk_order_index: ci,
          time_spent_sec: tickSec,
          interaction_done: false,
        },
        g
      )
        .then((data) => handleProgressApiResult(data))
        .catch(() => {});
    }, tickSec * 1000);
    return () => clearInterval(id);
  }, [tutorType, isConnected, lessonNo, grade, selectedSublessonNo, chunkOrderIndex, handleProgressApiResult]);

  // --------------------------
  // Load lessons
  // --------------------------
  useEffect(() => {
    if (tutorType !== "ai_tutor") return;
    if (studentProfileStatus !== "ready" || grade == null) return;

    const ac = new AbortController();

    async function loadLessons() {
      setLessonsError(null);
      setLessonsLoading(true);

      try {
        const url = new URL(LESSONS_ENDPOINT, AI_TUTOR_BASE);

        const r = await fetch(url.toString(), {
          signal: ac.signal,
          headers: getTutorHeaders(),
        });

        if (!r.ok) {
          const txt = await readErrText(r);
          throw new Error(`Lessons fetch failed: ${r.status} ${txt}`);
        }

        const data = await r.json();

        const list = Array.isArray(data?.lessons) ? data.lessons : [];
        const cleaned: LessonItem[] = list
          .filter(
            (x: any) =>
              x &&
              typeof x.lesson_no === "number" &&
              typeof x.lesson_title === "string"
          )
          .map((x: any) => {
            const sublessons = parseSublessonsFromApi(x.sublessons);
            const lesson_page_assets = parsePageAssetsFromApi(x.page_assets);
            const sublesson_count =
              typeof x.sublesson_count === "number" && Number.isFinite(x.sublesson_count)
                ? x.sublesson_count
                : sublessons.length;
            return {
              lesson_no: x.lesson_no,
              lesson_title: String(x.lesson_title || "").trim(),
              lesson_type: String(x.lesson_type || "").trim(),
              book_id:
                typeof x.book_id === "string" && x.book_id.trim() !== ""
                  ? x.book_id.trim()
                  : x.book_id ?? null,
              page_start: x.page_start,
              page_end: x.page_end,
              sublesson_count,
              sublessons: sublessons.length > 0 ? sublessons : undefined,
              ...(lesson_page_assets.length > 0 ? { page_assets: lesson_page_assets } : {}),
              section_key: x.section_key ?? null,
              topic_ta: x.topic_ta ?? null,
            };
          })
          .filter((x: any) => !!x.lesson_title);

        if (import.meta.env.DEV) {
          console.log("[AI Tutor] lessons API success: cleaned count:", cleaned.length, "first lesson_no:", cleaned[0]?.lesson_no, "current lessonNo before set:", lessonNo);
        }
        setLessons(cleaned);
        if (cleaned.length > 0 && (lessonNo == null || !cleaned.some((l) => l.lesson_no === lessonNo))) {
          setLessonNo(cleaned[0].lesson_no);
        }
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        if (isSchoolFeatureDisabledHandledError(e)) return;
        if (isAuthSessionExpiredHandledError(e)) return;
        setLessonsError(e?.message || "Failed to load lessons list.");
      } finally {
        setLessonsLoading(false);
        setLessonsLoadedOnce(true);
      }
    }

    loadLessons();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade, tutorType, studentProfileStatus]);

  const filteredLessons = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lessons;
    return lessons.filter((l) => {
      if (l.lesson_title.toLowerCase().includes(q)) return true;
      return (l.sublessons || []).some((s) => s.title.toLowerCase().includes(q));
    });
  }, [lessons, search]);

  useEffect(() => {
    if (import.meta.env.DEV && tutorType === "ai_tutor") {
      console.log("[AI Tutor] selected lessonNo:", lessonNo);
    }
  }, [tutorType, lessonNo]);

  const filteredTopics = useMemo(() => {
    const q = topicSearch.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter((t) => {
      if (t.title.toLowerCase().includes(q)) return true;
      const d = (t.description || "").toLowerCase();
      return d.includes(q);
    });
  }, [topics, topicSearch]);

  // --------------------------
  // Dialect list (Mongo prompt library; devteam-created profiles appear here)
  // Student-facing: filter & order by /me/school-language when available.
  // --------------------------
  useEffect(() => {
    const ac = new AbortController();

    function uniqueTrimmedKeys(keys?: string[] | null): string[] {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const raw of keys || []) {
        const key = String(raw || "").trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(key);
      }
      return out;
    }

    function orderDialectOptionsForSchool(
      allOptions: { key: string; name: string }[],
      schoolLanguage?: { preferred_dialect_keys?: string[]; default_dialect_key?: string | null } | null
    ): { key: string; name: string }[] {
      const allByKey = new Map(allOptions.map((d) => [d.key, d]));
      const preferredKeys = uniqueTrimmedKeys(schoolLanguage?.preferred_dialect_keys);

      if (preferredKeys.length === 0) {
        const standard = allByKey.get(DEFAULT_STUDENT_TUTOR_DIALECT_KEY);
        return standard ? [standard] : allOptions.slice(0, 1);
      }

      const ordered: { key: string; name: string }[] = [];
      for (const k of preferredKeys) {
        const o = allByKey.get(k);
        if (o) ordered.push(o);
      }

      // If none matched, fallback to standard or all
      if (ordered.length > 0) return ordered;
      const standard = allByKey.get(DEFAULT_STUDENT_TUTOR_DIALECT_KEY);
      return standard ? [standard] : allOptions.slice(0, 1);
    }

    function resolveStudentDefaultDialectKey(
      visibleOptions: { key: string; name: string }[],
      schoolLanguage?: { preferred_dialect_keys?: string[]; default_dialect_key?: string | null } | null
    ): string {
      const visibleKeys = new Set(visibleOptions.map((d) => d.key));
      const preferredKeys = uniqueTrimmedKeys(schoolLanguage?.preferred_dialect_keys);
      const configuredDefault = String(schoolLanguage?.default_dialect_key || "").trim();

      if (configuredDefault && visibleKeys.has(configuredDefault)) return configuredDefault;
      const firstPreferred = preferredKeys.find((k) => visibleKeys.has(k));
      if (firstPreferred) return firstPreferred;
      if (visibleKeys.has(DEFAULT_STUDENT_TUTOR_DIALECT_KEY)) return DEFAULT_STUDENT_TUTOR_DIALECT_KEY;
      return visibleOptions[0]?.key || DEFAULT_STUDENT_TUTOR_DIALECT_KEY;
    }

    async function loadDialectOptions() {
      try {
        const url = new URL(DIALECT_OPTIONS_ENDPOINT, AI_TUTOR_BASE);
        const dialectReq = fetch(url.toString(), { signal: ac.signal, headers: getTutorHeaders() });
        const schoolReq = getMySchoolLanguage(); // student wrapper; may fail

        const [dialectResSettled, schoolSettled] = await Promise.allSettled([dialectReq, schoolReq]);

        // Handle dialect options (required for names). If it failed, fallback to empty.
        let cleaned: { key: string; name: string }[] = [];
        if (dialectResSettled.status === "fulfilled") {
          try {
            const r = dialectResSettled.value;
            if (!r.ok) {
              if (import.meta.env.DEV) {
                console.warn("[TeacherLive] dialect-options request failed", r.status, r.statusText);
              }
            } else {
              const data = await r.json();
              const raw = Array.isArray(data?.dialects) ? data.dialects : [];
              cleaned = raw
                .filter((x: unknown) => x && typeof x === "object" && typeof (x as { key?: unknown }).key === "string")
                .map((x: { key: string; name?: string }) => ({
                  key: String(x.key).trim(),
                  name: String(x.name || x.key).trim() || String(x.key),
                }))
                .filter((x: { key: string; name: string }) => x.key.length > 0);
            }
          } catch (e) {
            if (import.meta.env.DEV) console.warn("[TeacherLive] dialect-options parse failed", e);
            cleaned = [];
          }
        } else {
          if (import.meta.env.DEV) console.warn("[TeacherLive] dialect-options fetch failed", dialectResSettled.reason);
          cleaned = [];
        }

        // If school language fetch succeeded, filter/order by preferred_dialect_keys.
        let visibleOptions: { key: string; name: string }[] = [];
        if (schoolSettled.status === "fulfilled" && cleaned.length > 0) {
          const schoolLanguage = schoolSettled.value;
          visibleOptions = orderDialectOptionsForSchool(cleaned, schoolLanguage as any);
          setDialectOptions(visibleOptions);
          // When school language is available, use backend default (do not preserve local prev on first load)
          setDialectKey(resolveStudentDefaultDialectKey(visibleOptions, schoolLanguage as any));
        } else {
          // School language failed or absent -> fallback to old behavior: show all cleaned dialects
          if (schoolSettled.status !== "fulfilled" && import.meta.env.DEV) {
            console.warn("[TeacherLive] getMySchoolLanguage failed — falling back to full dialect list", schoolSettled);
          }
          if (cleaned.length > 0) {
            setDialectOptions(cleaned);
            setDialectKey((prev) => pickDialectKeyFromServerList(prev, cleaned));
          } else {
            setDialectOptions([]);
          }
        }
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === "AbortError") return;
        if (import.meta.env.DEV) {
          console.warn("[TeacherLive] dialect-options could not be loaded", e);
        }
        setDialectOptions([]);
      }
    }

    void loadDialectOptions();
    return () => ac.abort();
  }, []);

  // --------------------------
  // Fetch prompt
  // --------------------------
  useEffect(() => {
    if (studentProfileStatus === "loading") {
      setConnState("idle");
      return;
    }
    if (studentProfileStatus === "error" || grade == null) {
      setConnState("idle");
      return;
    }
    if (tutorType === "ai_tutor" && lessonNo == null) {
      setConnState("idle");
      return;
    }

    const activeLessonForPrompt =
      tutorType === "ai_tutor" && lessonNo != null
        ? lessons.find((l) => l.lesson_no === lessonNo)
        : undefined;
    if (
      tutorType === "ai_tutor" &&
      activeLessonForPrompt &&
      hasSublessons(activeLessonForPrompt) &&
      !selectedSublessonNo
    ) {
      setConnState("idle");
      return;
    }

    // Wait for the practice topic list before fetching instruction so we do not race URL `topic_id`
    // with an empty selection, abort/retry, or flash a transient error while the panel still says "Loading practice…".
    if (tutorType === "communication_tutor" && topicsLoading) {
      setError(null);
      setConnState("idle");
      return;
    }

    const ac = new AbortController();

    async function loadPrompt() {
      const promptLessonNo = tutorType === "ai_tutor" ? lessonNo : null;
      if (import.meta.env.DEV && tutorType === "ai_tutor") {
        console.log(
          "[AI Tutor] fetching prompt for lessonNo:",
          promptLessonNo,
          "sublesson_no:",
          selectedSublessonNo,
          "URL will use lesson_no=" + String(promptLessonNo)
        );
      }

      const topicIdForRequest =
        tutorType === "communication_tutor"
          ? (selectedTopic?.topic_id || initialTopicId || "").trim()
          : "";
      const legacyTopicLabel =
        tutorType === "communication_tutor" && !topicIdForRequest
          ? (selectedTopic?.title || initialTopicLabel || "").trim()
          : "";

      if (tutorType === "communication_tutor" && !topicIdForRequest && !legacyTopicLabel) {
        setError(null);
        setSystemInstruction("");
        setLessonTitle("");
        setLessonStyle("");
        setLessonType("");
        setBodyParts([]);
        setConnState("idle");
        return;
      }

      setError(null);
      setSystemInstruction("");
      setLessonTitle("");
      setLessonStyle("");
      setLessonType("");
      setBodyParts([]);
      setConnState("loadingPrompt");

      try {
        let data: Record<string, unknown>;

        if (tutorType === "ai_tutor") {
          const url = new URL(AI_TUTOR_PROMPT_ENDPOINT, AI_TUTOR_BASE);
          url.searchParams.set("lesson_no", String(lessonNo));
          if (selectedSublessonNo) url.searchParams.set("sublesson_no", selectedSublessonNo);
          url.searchParams.set("dialect_key", dialectKey);

          const r = await fetch(url.toString(), {
            signal: ac.signal,
            headers: getTutorHeaders(),
          });

          if (!r.ok) {
            const txt = await readErrText(r);
            if (import.meta.env.DEV) {
              console.log(
                "[AI Tutor] prompt fetch failed: status:",
                r.status,
                "statusText:",
                r.statusText,
                "body:",
                txt,
                "lessonNo used:",
                promptLessonNo
              );
            }
            if (
              await handleSchoolFeatureDisabledIfPresent({
                status: r.status,
                bodyText: txt,
                navigate,
                refetchFeatureAccess,
                fallbackFeatureKey: tutorType,
              })
            ) {
              setConnState("idle");
              return;
            }
            if (
              handleTutorSessionTokenAuthExpiredIfPresent({
                status: r.status,
                bodyText: txt,
                navigate,
              })
            ) {
              throw new AuthSessionExpiredHandledError();
            }
            if (r.status === 404 && tutorType === "ai_tutor") {
              throw new Error(
                `Lesson or sublesson not found (404). Try another sublesson or refresh the lesson list. ${txt}`
              );
            }
            throw new Error(`Prompt fetch failed: ${r.status} ${txt}`);
          }

          data = (await r.json()) as Record<string, unknown>;
        } else {
          const comm = await getCommunicationInstruction(
            {
              topicId: topicIdForRequest || undefined,
              dialectKey,
              topicLabel: legacyTopicLabel || undefined,
            },
            { signal: ac.signal }
          );
          data = comm as unknown as Record<string, unknown>;
        }

        if (import.meta.env.DEV && tutorType === "ai_tutor") {
          console.log("[AI Tutor] prompt loaded for lessonNo:", promptLessonNo);
        }

        setLessonTitle(String(data.lesson_title || data.topic_title || ""));
        setLessonStyle(String(data.lesson_style || ""));
        setLessonType(String(data.lesson_type || ""));

        const rawParts = data?.body_parts;
        if (Array.isArray(rawParts)) {
          const cleaned: BodyPartItem[] = rawParts
            .filter((x: unknown) => x && typeof x === "object")
            .map((x: any) => ({
              id: String(x.id || "").trim(),
              label_ta: String(x.label_ta || "").trim(),
              image_url: x.image_url != null ? String(x.image_url).trim() : undefined,
            }))
            .filter((x: BodyPartItem) => x.id && x.label_ta);
          setBodyParts(cleaned);
        } else {
          setBodyParts([]);
        }

        const enhancedInstruction = `${String(data.systemInstruction || "")}

OUTPUT: Tamil speech only (no English). Short, clear, child-paced.`.trim();

        setSystemInstruction(enhancedInstruction);
        setConnState("ready");
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        if (isAxiosError(e) && e.code === "ERR_CANCELED") return;
        if (isSchoolFeatureDisabledHandledError(e)) {
          setConnState("idle");
          return;
        }
        if (isAuthSessionExpiredHandledError(e)) {
          setConnState("idle");
          return;
        }
        if (tutorType === "communication_tutor" && isAxiosError(e)) {
          const handled = await handleSchoolFeatureDisabledFromAxiosError(
            e,
            navigate,
            refetchFeatureAccess,
            "communication_tutor"
          );
          if (handled) {
            setConnState("idle");
            return;
          }
        }
        setError(e?.message || "Failed to load prompt.");
        setConnState("error");
        if (import.meta.env.DEV && tutorType === "ai_tutor") {
          console.log(
            "[AI Tutor] prompt fetch error (after catch): lessonNo used:",
            promptLessonNo,
            "error:",
            e?.message
          );
        }
      }
    }

    loadPrompt();
    return () => ac.abort();
  }, [
    studentProfileStatus,
    tutorType,
    grade,
    lessonNo,
    selectedSublessonNo,
    // chunkOrderIndex intentionally omitted: resume cursor / session/update must not refetch prompt or touch live UI.
    lessons,
    selectedTopic?.topic_id,
    selectedTopic?.title,
    initialTopicId,
    initialTopicLabel,
    dialectKey,
    navigate,
    refetchFeatureAccess,
    topicsLoading,
  ]);

  // Close TOC drawer whenever TOC should not be visible
  useEffect(() => {
    if (!showToc) setTocOpen(false);
  }, [showToc]);
  // Close topics drawer whenever topics panel should not be visible
  useEffect(() => {
    if (!showTopics) setTopicsDrawerOpen(false);
  }, [showTopics]);

  // Pre-select topic when initialTopicId is set and topics are loaded
  useEffect(() => {
    if (initialTopicId && topics.length > 0) {
      const topic = topics.find((t) => t.topic_id === initialTopicId);
      if (topic) setSelectedTopic(topic);
    }
  }, [initialTopicId, topics]);

  // Selected topic removed after a list refresh — clear selection and end any live session
  useEffect(() => {
    if (tutorType !== "communication_tutor" || !selectedTopic || topicsLoading) return;
    if (topics.length === 0) return;
    const stillListed = topics.some((t) => t.topic_id === selectedTopic.topic_id);
    if (!stillListed) {
      void disconnectSession();
      setSelectedTopic(null);
      setError("This topic is no longer available. Choose another topic.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- disconnectSession is stable enough for this guard
  }, [tutorType, selectedTopic, topics, topicsLoading]);

  // Switching conversation topic must not keep an old Live prompt active
  useEffect(() => {
    if (tutorType !== "communication_tutor") return;
    const tid = selectedTopic?.topic_id ?? null;
    if (prevCommTopicIdRef.current === null) {
      prevCommTopicIdRef.current = tid;
      return;
    }
    if (prevCommTopicIdRef.current !== tid) {
      prevCommTopicIdRef.current = tid;
      if (isConnected) void disconnectSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorType, selectedTopic?.topic_id, isConnected]);

  /** Disconnect Live when lesson/sublesson changes mid-session (resume/TOC edge cases). */
  useEffect(() => {
    if (tutorType !== "ai_tutor") return;
    if (!isConnected) {
      connectedLessonKeyRef.current = null;
      return;
    }
    const key = { lessonNo, sublessonNo: selectedSublessonNo };
    const prev = connectedLessonKeyRef.current;
    if (prev === null) {
      connectedLessonKeyRef.current = key;
      return;
    }
    if (prev.lessonNo !== lessonNo || prev.sublessonNo !== selectedSublessonNo) {
      connectedLessonKeyRef.current = key;
      void disconnectSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorType, isConnected, lessonNo, selectedSublessonNo]);

  // --------------------------
  // Cleanup
  // --------------------------
  function clearTeacherSpeakingWatchdog() {
    if (teacherSpeakingWatchdogRef.current != null) {
      window.clearTimeout(teacherSpeakingWatchdogRef.current);
      teacherSpeakingWatchdogRef.current = null;
    }
  }

  /** Clears stuck "Teacher speaking…" if no buffers are actually playing. */
  function scheduleTeacherSpeakingWatchdog() {
    clearTeacherSpeakingWatchdog();
    teacherSpeakingWatchdogRef.current = window.setTimeout(() => {
      teacherSpeakingWatchdogRef.current = null;
      if (livePhaseRef.current !== "connected") return;
      if (sourcesRef.current.size > 0) {
        const ctx = audioContextRef.current;
        if (
          import.meta.env.DEV &&
          ctx &&
          ctx.state !== "closed" &&
          nextStartTimeRef.current - ctx.currentTime > 3
        ) {
          console.warn("[live-audio] speaking watchdog: schedule far ahead of currentTime", {
            nextStartTime: nextStartTimeRef.current,
            currentTime: ctx.currentTime,
            activeSources: sourcesRef.current.size,
          });
        }
        return;
      }
      isTeacherSpeakingRef.current = false;
      setIsTeacherSpeaking(false);
    }, TEACHER_SPEAKING_WATCHDOG_MS);
  }

  /** Resets playback timeline; optionally aligns to open output AudioContext (Picture Talk pattern). */
  function resetTeacherAudioPipeline(opts?: { alignToContext?: boolean }) {
    const ctx = audioContextRef.current;
    nextStartTimeRef.current =
      opts?.alignToContext && ctx && ctx.state !== "closed" ? ctx.currentTime : 0;
  }

  function stopTimer() {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    hasTimerStartedRef.current = false;
    setTimeLeft(TOTAL_TIME);
  }

  function stopAllAudioSources() {
    clearTeacherSpeakingWatchdog();
    sourcesRef.current.forEach((src) => {
      try {
        src.stop();
      } catch {}
    });
    sourcesRef.current.clear();
    isTeacherSpeakingRef.current = false;
    setIsTeacherSpeaking(false);
    resetTeacherAudioPipeline({ alignToContext: true });
  }

  /** Stops mic capture, tears down ScriptProcessor graph, closes input/output AudioContexts. Idempotent. */
  async function cleanupLiveAudioResources() {
    stopAllAudioSources();
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {}
    try {
      processorRef.current?.disconnect();
    } catch {}
    try {
      inputSourceRef.current?.disconnect();
    } catch {}
    processorRef.current = null;
    inputSourceRef.current = null;
    try {
      await audioContextRef.current?.close();
    } catch {}
    try {
      await inputAudioContextRef.current?.close();
    } catch {}
    audioContextRef.current = null;
    inputAudioContextRef.current = null;
    streamRef.current = null;
  }

  /**
   * When the Live WebSocket closes or errors without going through `disconnectSession`,
   * mirror that full teardown so queued audio, mic, and refs cannot outlive the UI.
   */
  async function finalizeLiveSocketUnexpectedEnd(opts: {
    errorMessage?: string | null;
    closedBeforeOpen?: boolean;
  }) {
    if (liveUnexpectedTeardownLockRef.current) return;
    liveUnexpectedTeardownLockRef.current = true;
    livePhaseRef.current = opts.errorMessage ? "error" : "idle";
    try {
      if (bodyPartsGreetingTimerRef.current) {
        window.clearTimeout(bodyPartsGreetingTimerRef.current);
        bodyPartsGreetingTimerRef.current = null;
      }
      sessionPromiseRef.current = null;
      liveSessionRef.current = null;
      await cleanupLiveAudioResources();
      setUserIsSpeaking(false);
      setIsMuted(true);
      didOpenRef.current = false;
      connectLockRef.current = false;
      if (opts.errorMessage) {
        setLivePhase("error");
        setConnState("error");
        setError(opts.errorMessage);
      } else {
        setLivePhase("idle");
        setConnState("ready");
        if (opts.closedBeforeOpen) {
          setError("WebSocket closed before open.");
        } else {
          setError("Live session ended unexpectedly.");
        }
      }
      endSession();
    } catch (e) {
      console.error("[TeacherLive] finalizeLiveSocketUnexpectedEnd failed", e);
    }
  }

  async function disconnectSession(options?: { resetTimer?: boolean }) {
    const resetTimer = options?.resetTimer !== false;

    clearCommPersistTimer();
    flushCommunicationPersist();

    liveUnexpectedTeardownLockRef.current = false;
    livePhaseRef.current = "disconnecting";
    setLivePhase("disconnecting");
    intentionalDisconnectRef.current = true;
    stopAllAudioSources();

    commModelTextDraftRef.current = "";
    commModelHadAudioRef.current = false;
    communicationTurnInFlightRef.current = false;

    clientSilenceMsRef.current = 0;
    clientVadEndSentRef.current = false;

    if (bodyPartsGreetingTimerRef.current) {
      window.clearTimeout(bodyPartsGreetingTimerRef.current);
      bodyPartsGreetingTimerRef.current = null;
    }

    let closedWebSocket = false;
    try {
      const sp = sessionPromiseRef.current;
      if (sp) {
        const s = await sp;
        try {
          s.close();
          closedWebSocket = true;
        } catch {}
      }
    } catch {}

    sessionPromiseRef.current = null;
    liveSessionRef.current = null;

    await cleanupLiveAudioResources();
    nextStartTimeRef.current = 0;

    if (resetTimer) {
      stopTimer();
    } else {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      hasTimerStartedRef.current = false;
    }

    setUserIsSpeaking(false);
    setIsMuted(true);
    didOpenRef.current = false;
    connectLockRef.current = false;

    if (!closedWebSocket) {
      intentionalDisconnectRef.current = false;
    }

    // Tracking: submit summary on disconnect
    endSession();

    livePhaseRef.current = "idle";
    setLivePhase("idle");
    setConnState("ready");
  }

  async function onLessonRowActivated(l: LessonItem) {
    if (tutorType !== "ai_tutor") return;
    if (isConnected) await disconnectSession();
    const sub = hasSublessons(l);
    setLessonNo(l.lesson_no);
    if (sub) {
      setExpandedLessonNos((prev) => ({ ...prev, [l.lesson_no]: !prev[l.lesson_no] }));
      setSelectedSublessonNo((prev) => resolveSublessonNoForLesson(l, prev));
      setChunkOrderIndex(0);
    } else {
      setSelectedSublessonNo(null);
      setChunkOrderIndex(null);
      setTocOpen(false);
    }
  }

  async function onSublessonActivated(l: LessonItem, sub: TutorSublessonItem) {
    if (tutorType !== "ai_tutor") return;
    if (isConnected) await disconnectSession();
    setLessonNo(l.lesson_no);
    setExpandedLessonNos((prev) => ({ ...prev, [l.lesson_no]: true }));
    setSelectedSublessonNo(sub.sublesson_no);
    setChunkOrderIndex(0);
    setTocOpen(false);
  }

  async function stepSublesson(delta: number) {
    if (!currentLesson || !hasSublessons(currentLesson) || !selectedSublessonNo) return;
    const subs = sortedSublessons(currentLesson);
    const idx = subs.findIndex((s) => s.sublesson_no === selectedSublessonNo);
    const next = subs[idx + delta];
    if (!next) return;
    if (isConnected) await disconnectSession();
    setSelectedSublessonNo(next.sublesson_no);
    setChunkOrderIndex(0);
  }

  const sublessonNavIndex =
    currentLesson && selectedSublessonNo
      ? sortedSublessons(currentLesson).findIndex((s) => s.sublesson_no === selectedSublessonNo)
      : -1;
  const sublessonNavList = currentLesson && hasSublessons(currentLesson) ? sortedSublessons(currentLesson) : [];

  // Unmount / strict-mode: tear down Live session, mic graph, and AudioContexts (idempotent via disconnectSession).
  useEffect(() => {
    return () => {
      void disconnectSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function explainBodyPart(part: BodyPartItem) {
    setActiveBodyPartId(part.id);
    if (!isConnected) {
      setError("First click Start Class, listen for the greeting, then tap a picture.");
      return;
    }
    let sess: unknown = liveSessionRef.current;
    if (!sess && sessionPromiseRef.current) {
      try {
        sess = await sessionPromiseRef.current;
        liveSessionRef.current = sess;
      } catch {
        sess = null;
      }
    }
    const send = pickSendClientContent(sess);
    if (!send) {
      setError("Could not reach the tutor session. Try disconnect and Start Class again.");
      return;
    }
    setError(null);
    try {
      recordTurnWithProgress(
        "user",
        `Selected body part: ${part.label_ta} (${part.id})`
      );
      await send(
        `[STUDENT_ACTION] Tapped card: ${part.label_ta} (id: ${part.id}). Teach this part now.`
      );
    } catch (e: unknown) {
      setError(fmtErr(e));
    }
  }

  async function fetchEphemeralToken(): Promise<string> {
    const url = new URL(TOKEN_ENDPOINT, AI_TUTOR_BASE);

    const tokenRes = await fetch(url.toString(), {
      method: "GET",
      headers: getTutorHeaders(),
    });

    if (!tokenRes.ok) {
      const txt = await readErrText(tokenRes);
      if (
        await handleSchoolFeatureDisabledIfPresent({
          status: tokenRes.status,
          bodyText: txt,
          navigate,
          refetchFeatureAccess,
          fallbackFeatureKey: tutorType,
        })
      ) {
        throw new SchoolFeatureDisabledHandledError();
      }
      if (
        handleTutorSessionTokenAuthExpiredIfPresent({
          status: tokenRes.status,
          bodyText: txt,
          navigate,
        })
      ) {
        throw new AuthSessionExpiredHandledError();
      }
      throw new Error(`Token fetch failed: ${tokenRes.status} ${txt}`);
    }

    const data = await tokenRes.json();
    const token = data?.token;
    if (!token || typeof token !== "string") throw new Error("Invalid token from backend.");
    return token;
  }

  function fmtErr(e: any) {
    if (!e) return "Unknown Live error";
    if (typeof e === "string") return e;
    try {
      return e?.message || JSON.stringify(e);
    } catch {
      return String(e);
    }
  }

  async function connectSession() {
    if (connectLockRef.current) return;
    connectLockRef.current = true;
    liveUnexpectedTeardownLockRef.current = false;

    try {
      setError(null);

      if (!systemInstruction) {
        setError("System instruction is empty. Check backend.");
        setConnState("error");
        livePhaseRef.current = "idle";
        setLivePhase("idle");
        connectLockRef.current = false;
        return;
      }

      setConnState("connecting");
      await disconnectSession();
      commModelTextDraftRef.current = "";
      commModelHadAudioRef.current = false;
      communicationTurnInFlightRef.current = false;
      livePhaseRef.current = "connecting";
      setLivePhase("connecting");

      // Parallel: token + mic (saves serial wait before Live connect).
      const settled = await Promise.allSettled([
        fetchEphemeralToken(),
        navigator.mediaDevices.getUserMedia({ audio: true }),
      ]);
      const tokenRes = settled[0];
      const micRes = settled[1];
      if (tokenRes.status !== "fulfilled") {
        if (micRes.status === "fulfilled") {
          micRes.value.getTracks().forEach((t) => t.stop());
        }
        throw tokenRes.reason;
      }
      if (micRes.status !== "fulfilled") {
        throw micRes.reason;
      }
      const token = tokenRes.value;
      const stream = micRes.value;
      streamRef.current = stream;

      const AudioClass = window.AudioContext || (window as any).webkitAudioContext;
      const outputCtx = new AudioClass({ sampleRate: OUTPUT_SAMPLE_RATE });
      const inputCtx = new AudioClass({
        sampleRate: INPUT_AUDIO_CONTEXT_RATE,
        latencyHint: "interactive",
      });

      audioContextRef.current = outputCtx;
      inputAudioContextRef.current = inputCtx;

      if (outputCtx.state === "suspended") await outputCtx.resume();
      if (inputCtx.state === "suspended") await inputCtx.resume();

      nextStartTimeRef.current = outputCtx.currentTime;

      liveConnectGenerationRef.current += 1;
      const connectGen = liveConnectGenerationRef.current;

      const ai = new GoogleGenAI({
        apiKey: token,
        httpOptions: { apiVersion: "v1alpha" },
      });

      const sp = ai.live.connect({
        model: MODEL,
        config: {
          systemInstruction,
          responseModalities: [Modality.AUDIO],
          // 2.5 native audio: thinking on by default — adds delay before first spoken token.
          thinkingConfig: { thinkingBudget: 0 },
          // Student speech → text for session summary / rubric (server sends inputTranscription).
          inputAudioTranscription: {},
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: VOICE } },
          },
          realtimeInputConfig: {
            automaticActivityDetection: {
              startOfSpeechSensitivity: StartSensitivity.START_SENSITIVITY_HIGH,
              endOfSpeechSensitivity: EndSensitivity.END_SENSITIVITY_HIGH,
              prefixPaddingMs: 20,
              silenceDurationMs: isBodyPartsLesson ? BODY_PARTS_VAD_SILENCE_MS : 800,
            },
          },
        },
        callbacks: {
          onopen: () => {
            if (liveConnectGenerationRef.current !== connectGen) return;
            didOpenRef.current = true;
            livePhaseRef.current = "connected";
            setConnState("connected");
            setLivePhase("connected");
            setError(null);
            void sp.then(async (sess: unknown) => {
              if (liveConnectGenerationRef.current !== connectGen) return;
              liveSessionRef.current = sess;
              // Body-parts: delay first nudge so the student has ~2s to speak before the teacher greets.
              if (tutorType === "ai_tutor" && isBodyPartsLesson) {
                if (bodyPartsGreetingTimerRef.current) {
                  window.clearTimeout(bodyPartsGreetingTimerRef.current);
                }
                bodyPartsGreetingTimerRef.current = window.setTimeout(() => {
                  bodyPartsGreetingTimerRef.current = null;
                  void (async () => {
                    if (liveSessionRef.current !== sess) return;
                    const send = pickSendClientContent(sess);
                    if (!send) return;
                    try {
                      await send(
                        "Class just started. The student sees body-part picture cards. Mic is off by default until they choose to unmute. " +
                          "Follow SESSION_OPENING in your system instructions first: welcome the student by name in Tamil if a name was given, " +
                          "give the tiny lesson intro for body-parts cards, then invite them to tap ONE picture. " +
                          "If you already heard them speak, acknowledge briefly in Tamil, then ask them to tap ONE picture. " +
                          "Otherwise give that greeting + intro, then ask them to tap ONE picture/card first. " +
                          "Do not teach any body part until they tap a card."
                      );
                    } catch {
                      /* non-fatal */
                    }
                  })();
                }, BODY_PARTS_START_GREETING_DELAY_MS);
              }
            });

            // Tracking: begin session — FREE or TOPIC, never LESSON here
            beginSession({
              mode: "VOICE",
              tutorType: tutorType === "ai_tutor" ? "FREE" : "TOPIC",
              lessonNo: tutorType === "ai_tutor" && lessonNo ? lessonNo : undefined,
              sublessonNo:
                tutorType === "ai_tutor" && selectedSublessonNo ? selectedSublessonNo : undefined,
              topicLabel:
                tutorType === "communication_tutor"
                  ? (selectedTopic?.title ||
                      lessonTitle ||
                      initialTopicLabel ||
                      undefined)
                  : undefined,
            });
            if (
              tutorType === "ai_tutor" &&
              lessonNo != null &&
              grade != null &&
              selectedSublessonNo &&
              chunkOrderIndex != null
            ) {
              void postTutorSessionPosition(
                {
                  lesson_no: lessonNo,
                  sublesson_no: selectedSublessonNo,
                  chunk_order_index: chunkOrderIndex,
                },
                grade
              ).catch((e: unknown) => {
                if (import.meta.env.DEV) {
                  console.warn("[TeacherLive] session/update on session open (non-fatal)", {
                    lesson_no: lessonNo,
                    sublesson_no: selectedSublessonNo,
                    chunk_order_index: chunkOrderIndex,
                    err: e,
                  });
                }
              });
            }
            // Notify parent that session became active (optional hook)
            try {
              if (onSessionStart) onSessionStart();
            } catch {}

            clientSilenceMsRef.current = 0;
            clientVadEndSentRef.current = false;

            const source = inputCtx.createMediaStreamSource(stream);
            const processor = inputCtx.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);

            processor.onaudioprocess = (e) => {
              if (liveConnectGenerationRef.current !== connectGen) return;
              if (livePhaseRef.current !== "connected") return;

              if (isMutedRef.current) {
                setUserIsSpeaking(false);
                clientSilenceMsRef.current = 0;
                return;
              }

              if (isTeacherSpeakingRef.current) {
                clientSilenceMsRef.current = 0;
                clientVadEndSentRef.current = false;
                return;
              }

              const inputBuffer = e.inputBuffer;
              const inputData = inputBuffer.getChannelData(0);
              const rms = Math.sqrt(
                inputData.reduce((s, v) => s + v * v, 0) / inputData.length
              );
              setUserIsSpeaking(rms > 0.02);

              const pcmBlob = createBlob(inputData);
              sp.then((sess: any) => {
                if (liveConnectGenerationRef.current !== connectGen) return;
                if (liveSessionRef.current !== sess) return;
                if (isMutedRef.current || isTeacherSpeakingRef.current) return;
                sess.sendRealtimeInput({ media: pcmBlob }).catch(() => {});
              }).catch(() => {});

              if (!didOpenRef.current) return;

              const bufferMs = (inputData.length / INPUT_AUDIO_CONTEXT_RATE) * 1000;
              if (rms < CLIENT_VAD_SILENCE_RMS) {
                clientSilenceMsRef.current += bufferMs;
                if (
                  clientSilenceMsRef.current >= CLIENT_VAD_SILENCE_MS &&
                  !clientVadEndSentRef.current
                ) {
                  clientVadEndSentRef.current = true;
                  sp.then((sess: any) => {
                    if (liveConnectGenerationRef.current !== connectGen) return;
                    if (liveSessionRef.current !== sess) return;
                    if (isMutedRef.current || isTeacherSpeakingRef.current) return;
                    sess.sendRealtimeInput({ audioStreamEnd: true }).catch(() => {});
                  }).catch(() => {});
                }
              } else {
                clientSilenceMsRef.current = 0;
                clientVadEndSentRef.current = false;
              }
            };

            source.connect(processor);
            processor.connect(inputCtx.destination);

            inputSourceRef.current = source;
            processorRef.current = processor;
          },

          onmessage: async (message: LiveServerMessage) => {
            if (liveConnectGenerationRef.current !== connectGen) return;
            if (livePhaseRef.current !== "connected") return;

            const sc = message?.serverContent as Record<string, unknown> | undefined;
            if (sc?.interrupted) {
              stopAllAudioSources();
              resetTeacherAudioPipeline({ alignToContext: true });
              flushCommunicationPersist();
            }

            const inputTx = sc?.inputTranscription ?? sc?.input_transcription;
            if (inputTx && typeof inputTx === "object") {
              const tx = inputTx as {
                text?: string;
                transcript?: string;
                finished?: boolean;
                isFinal?: boolean;
              };
              const u = String(tx.text ?? tx.transcript ?? "").trim();
              const explicitlyNonFinal = tx.finished === false || tx.isFinal === false;
              if (u && !explicitlyNonFinal) recordTurnWithProgress("user", u);
            }

            const parts = message?.serverContent?.modelTurn?.parts || [];
            const p = parts.find((x: any) => x?.inlineData?.data);
            const base64Audio = p?.inlineData?.data;

            // Tracking: capture model text turns if Gemini Live returns them
            const textParts = parts.filter(
              (x: any) => typeof x?.text === "string" && x.text.trim()
            );
            textParts.forEach((x: any) => recordTurnWithProgress("model", x.text.trim()));
            if (tutorType === "communication_tutor") {
              if (textParts.length > 0) {
                const last = textParts[textParts.length - 1] as { text: string };
                commModelTextDraftRef.current = String(last.text ?? "").trim();
              }
              if (base64Audio) {
                commModelHadAudioRef.current = true;
              }
            }

            const turnComplete = Boolean(sc?.turnComplete ?? sc?.turn_complete);
            if (turnComplete) {
              flushCommunicationTurnFromLive("turn_complete");
            }

            if (!base64Audio) return;
            if (liveConnectGenerationRef.current !== connectGen) return;
            if (livePhaseRef.current !== "connected") return;

            const payloadSize =
              typeof base64Audio === "string" ? base64Audio.length : String(base64Audio).length;

            const getOutputCtxOrRecreate = (): AudioContext | null => {
              let ctx = audioContextRef.current;
              if (ctx && ctx.state !== "closed") return ctx;
              try {
                const AudioClass = window.AudioContext || (window as any).webkitAudioContext;
                ctx = new AudioClass({ sampleRate: OUTPUT_SAMPLE_RATE });
                audioContextRef.current = ctx;
                resetTeacherAudioPipeline({ alignToContext: false });
                return ctx;
              } catch {
                return null;
              }
            };

            let audioCtx = getOutputCtxOrRecreate();
            if (!audioCtx) {
              console.warn("[live-audio] no usable output AudioContext", {
                hasCtx: !!audioContextRef.current,
                state: audioContextRef.current?.state,
                nextStartTime: nextStartTimeRef.current,
                payloadSize,
              });
              return;
            }

            const decodeCtx = audioCtx;
            let buffer: AudioBuffer;
            try {
              buffer = await decodeAudioData(
                decode(base64Audio),
                decodeCtx,
                OUTPUT_SAMPLE_RATE,
                1
              );
            } catch (error) {
              console.warn("[live-audio] decode failed", {
                state: decodeCtx.state,
                error,
                hasCtx: !!decodeCtx,
                nextStartTime: nextStartTimeRef.current,
                payloadSize,
              });
              if (sourcesRef.current.size === 0) {
                isTeacherSpeakingRef.current = false;
                setIsTeacherSpeaking(false);
              }
              return;
            }

            if (liveConnectGenerationRef.current !== connectGen) return;
            if (livePhaseRef.current !== "connected") return;
            if (audioContextRef.current !== decodeCtx) return;

            audioCtx = audioContextRef.current;
            if (!audioCtx || audioCtx.state === "closed") {
              console.warn("[live-audio] output context missing or closed after decode", {
                hasCtx: !!audioCtx,
                state: audioCtx?.state,
                nextStartTime: nextStartTimeRef.current,
                payloadSize,
              });
              if (sourcesRef.current.size === 0) {
                isTeacherSpeakingRef.current = false;
                setIsTeacherSpeaking(false);
              }
              return;
            }

            try {
              if (audioCtx.state !== "running") await audioCtx.resume();
            } catch (error) {
              console.warn("[live-audio] resume failed", {
                state: audioCtx.state,
                error,
                hasCtx: !!audioCtx,
                nextStartTime: nextStartTimeRef.current,
                payloadSize,
              });
            }

            if (liveConnectGenerationRef.current !== connectGen) return;
            if (audioContextRef.current !== audioCtx) return;

            let src: AudioBufferSourceNode | null = null;
            try {
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, audioCtx.currentTime);
              if (
                import.meta.env.DEV &&
                nextStartTimeRef.current - audioCtx.currentTime > 2
              ) {
                console.warn("[live-audio] schedule far in future", {
                  nextStartTime: nextStartTimeRef.current,
                  currentTime: audioCtx.currentTime,
                  connectGen,
                });
              }
              src = audioCtx.createBufferSource();
              src.buffer = buffer;
              src.connect(audioCtx.destination);

              src.onended = () => {
                sourcesRef.current.delete(src!);
                if (sourcesRef.current.size === 0) {
                  clearTeacherSpeakingWatchdog();
                  isTeacherSpeakingRef.current = false;
                  setIsTeacherSpeaking(false);
                }
              };

              src.start(nextStartTimeRef.current);
              nextStartTimeRef.current += buffer.duration;
              sourcesRef.current.add(src);

              isTeacherSpeakingRef.current = true;
              setIsTeacherSpeaking(true);
              scheduleTeacherSpeakingWatchdog();
            } catch (error) {
              console.warn("[live-audio] playback failed", {
                state: audioCtx.state,
                error,
                hasCtx: !!audioCtx,
                nextStartTime: nextStartTimeRef.current,
                payloadSize,
              });
              try {
                src?.disconnect();
              } catch {}
              if (sourcesRef.current.size === 0) {
                isTeacherSpeakingRef.current = false;
                setIsTeacherSpeaking(false);
              }
            }
          },

          onclose: () => {
            const hadOpened = didOpenRef.current;
            connectLockRef.current = false;
            if (intentionalDisconnectRef.current) {
              intentionalDisconnectRef.current = false;
              setError(null);
              livePhaseRef.current = "idle";
              setLivePhase("idle");
              setConnState("ready");
              return;
            }
            void finalizeLiveSocketUnexpectedEnd({
              closedBeforeOpen: !hadOpened,
              errorMessage: null,
            });
          },

          onerror: (err: any) => {
            connectLockRef.current = false;
            void finalizeLiveSocketUnexpectedEnd({
              errorMessage: fmtErr(err),
              closedBeforeOpen: !didOpenRef.current,
            });
          },
        },
      });

      sessionPromiseRef.current = sp;

      sp.catch((e: any) => {
        if (isSchoolFeatureDisabledHandledError(e)) {
          void (async () => {
            await cleanupLiveAudioResources();
            setConnState("ready");
            livePhaseRef.current = "idle";
            setLivePhase("idle");
            connectLockRef.current = false;
          })();
          return;
        }
        if (isAuthSessionExpiredHandledError(e)) {
          void (async () => {
            await cleanupLiveAudioResources();
            setConnState("ready");
            livePhaseRef.current = "idle";
            setLivePhase("idle");
            connectLockRef.current = false;
          })();
          return;
        }
        connectLockRef.current = false;
        void finalizeLiveSocketUnexpectedEnd({
          errorMessage: fmtErr(e),
          closedBeforeOpen: !didOpenRef.current,
        });
      });
    } catch (e: any) {
      await cleanupLiveAudioResources();
      if (isSchoolFeatureDisabledHandledError(e)) {
        setConnState("ready");
        livePhaseRef.current = "idle";
        setLivePhase("idle");
        connectLockRef.current = false;
        return;
      }
      if (isAuthSessionExpiredHandledError(e)) {
        setConnState("ready");
        livePhaseRef.current = "idle";
        setLivePhase("idle");
        connectLockRef.current = false;
        return;
      }
      setError(fmtErr(e));
      setConnState("error");
      livePhaseRef.current = "error";
      setLivePhase("error");
      connectLockRef.current = false;
    }
  }

  useEffect(() => {
    if (livePhase !== "connected") return;
    if (!isMuted && !hasTimerStartedRef.current) {
      hasTimerStartedRef.current = true;
      timerRef.current = window.setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            window.clearInterval(timerRef.current!);
            timerRef.current = null;
            hasTimerStartedRef.current = false;
            setShowEndPopup(true);
            queueMicrotask(() => {
              void disconnectSession({ resetTimer: false });
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [isMuted, livePhase]);

  /** Reusable TOC list content for sidebar + mobile drawer */
  const tocContent = (
    <>
      <div className="shrink-0 p-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-slate-600" />
            <h2 className="text-sm font-extrabold text-slate-900">Lessons</h2>
          </div>
          <button
            type="button"
            onClick={() => setTocOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Close lessons"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          Open a lesson to see parts; disconnect to change lesson or sublesson.
        </p>
        <div className="mt-3 relative">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search lesson..."
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={tutorType !== "ai_tutor" || busy || isConnected || !profileReady}
          />
        </div>
        {isConnected && (
          <div className="mt-3 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
            Disconnect to change lessons or sublessons.
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-gradient-to-b from-blue-50 via-white to-blue-50 tocScroll">
        <style>{`
          .tocScroll::-webkit-scrollbar { width: 10px; }
          .tocScroll::-webkit-scrollbar-track { background: rgba(59,130,246,0.12); border-radius: 999px; }
          .tocScroll::-webkit-scrollbar-thumb { background: rgba(37,99,235,0.35); border-radius: 999px; }
          .tocScroll::-webkit-scrollbar-thumb:hover { background: rgba(37,99,235,0.55); }
        `}</style>
        {studentProfileStatus === "error" && studentProfileError ? (
          <div className="p-4 text-sm text-red-600">{studentProfileError}</div>
        ) : studentProfileStatus === "loading" && showToc ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" aria-hidden />
            <p className="text-sm text-slate-500">Loading your profile…</p>
          </div>
        ) : lessonsLoading || (showToc && !lessonsLoadedOnce) ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" aria-hidden />
            <p className="text-sm text-slate-500">Loading lessons...</p>
          </div>
        ) : lessonsError ? (
          <div className="p-4 text-sm text-red-600">{lessonsError}</div>
        ) : lessonsLoadedOnce && filteredLessons.length === 0 ? (
          <div className="p-4 text-sm text-slate-600">No lessons found.</div>
        ) : (
          <div className="space-y-2">
            {filteredLessons.map((l) => {
              const activeLesson = l.lesson_no === lessonNo;
              const expanded = !!expandedLessonNos[l.lesson_no];
              const subList = hasSublessons(l) ? sortedSublessons(l) : [];
              const lt = normalizeLessonType(l.lesson_type);
              return (
                <div key={l.lesson_no} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => void onLessonRowActivated(l)}
                    disabled={tutorType !== "ai_tutor" || busy || isConnected || !profileReady}
                    className={`w-full text-left rounded-2xl px-3 py-3 border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                      activeLesson
                        ? "bg-blue-100/80 border-blue-300 shadow-sm"
                        : "bg-white border-slate-200 hover:bg-blue-50 hover:border-blue-200"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {subList.length > 0 ? (
                          <span className="shrink-0 mt-0.5 text-slate-500" aria-hidden>
                            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </span>
                        ) : null}
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-emerald-700 leading-snug">{l.lesson_title}</div>
                          <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                            {l.page_start || l.page_end ? (
                              <div className="text-[11px] text-slate-600">
                                Pages {l.page_start ?? "?"}-{l.page_end ?? "?"}
                              </div>
                            ) : (
                              <div />
                            )}
                            {!!lt && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 font-extrabold">
                                {lt}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {activeLesson && <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-blue-600 shrink-0" />}
                    </div>
                  </button>
                  {subList.length > 0 && expanded && (
                    <div className="pl-2 space-y-1 border-l-2 border-blue-200 ml-3">
                      {subList.map((sub) => {
                        const activeSub =
                          lessonNo === l.lesson_no && selectedSublessonNo === sub.sublesson_no;
                        return (
                          <button
                            key={sub.sublesson_no}
                            type="button"
                            onClick={() => void onSublessonActivated(l, sub)}
                            disabled={tutorType !== "ai_tutor" || busy || isConnected || !profileReady}
                            className={`w-full text-left rounded-xl px-3 py-2 text-xs border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                              activeSub
                                ? "bg-emerald-100/90 border-emerald-400 font-extrabold text-emerald-900"
                                : "bg-white border-slate-200 text-slate-800 hover:bg-emerald-50/80"
                            }`}
                          >
                            <span className="text-[10px] text-slate-500 font-mono mr-1">{sub.sublesson_no}</span>
                            {sub.title}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* <div className="shrink-0 p-3 border-t border-slate-200 text-[10px] text-slate-500">Tip: Use search to quickly jump to a lesson title.</div> */}
    </>
  );

  /** Topics panel content for Communication Tutor */
  const topicsContent = (
    <>
      <div className="shrink-0 p-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} className="text-indigo-600" />
            <h2 className="text-sm font-extrabold text-slate-900">Practice</h2>
          </div>
          <button
            type="button"
            onClick={() => setTopicsDrawerOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Close practice list"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1 font-semibold">Choose what to practice</p>
        <div className="mt-3 relative">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            value={topicSearch}
            onChange={(e) => setTopicSearch(e.target.value)}
            placeholder="Search practice..."
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={busy || isConnected || !profileReady}
          />
        </div>
        {isConnected && (
          <div className="mt-3 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
            Disconnect to change practice.
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-gradient-to-b from-indigo-50 via-white to-indigo-50 tocScroll">
        {studentProfileStatus === "error" && studentProfileError ? (
          <div className="p-4 text-sm text-red-600">{studentProfileError}</div>
        ) : studentProfileStatus === "loading" ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-2" />
            <span className="text-sm">Loading your profile…</span>
          </div>
        ) : topicsLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-2" />
            <span className="text-sm">Loading practice…</span>
          </div>
        ) : filteredTopics.length === 0 ? (
          <div className="p-4 text-sm text-slate-600">No practice topics available for this grade</div>
        ) : (
          <div className="space-y-2">
            {filteredTopics.map((t) => {
              const active = selectedTopic?.topic_id === t.topic_id;
              return (
                <button
                  key={t.topic_id}
                  onClick={() => {
                    setSelectedTopic(t);
                    setTopicsDrawerOpen(false);
                  }}
                  disabled={busy || isConnected || !profileReady}
                  className={`w-full text-left rounded-2xl px-3 py-3 border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    active ? "bg-indigo-100/80 border-indigo-300 shadow-sm" : "bg-white border-slate-200 hover:bg-indigo-50 hover:border-indigo-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-extrabold text-indigo-700 leading-snug">{t.title}</div>
                    {active && <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-indigo-600" />}
                  </div>
                  {t.description && (
                    <p
                      className={`mt-1 text-[11px] line-clamp-2 ${
                        active ? "font-semibold text-indigo-800" : "text-slate-600"
                      }`}
                    >
                      {t.description}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return (
    <Layout noPadding fillParent showSidebar={!hideSidebar}>
      <div className="h-full min-h-0 flex flex-col bg-fuchsia-50/40 text-slate-900 font-sans overflow-hidden">
        <div className="flex-1 min-h-0 grid grid-cols-12 overflow-hidden">
          {/* MAIN */}
          <div
            className={`col-span-12 ${(showToc || showTopics) ? "lg:col-span-9" : "lg:col-span-12"} flex flex-col h-full min-h-0 overflow-hidden`}
          >
            {/* LIGHT HEADER (responsive reflow) */}
            <div className="shrink-0 px-3 py-3 sm:px-6 sm:py-4 bg-white border-b border-slate-200 flex flex-col md:flex-row flex-wrap items-center justify-between gap-2 sm:gap-3">
              <div className="min-w-0 w-full md:w-auto">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-extrabold text-base sm:text-lg">Tamil Teacher</h1>
                  {studentProfileStatus === "loading" ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 border border-slate-200 shrink-0 text-slate-500">
                      Grade …
                    </span>
                  ) : studentProfileStatus === "error" ? null : grade != null ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 border border-slate-200 shrink-0">
                      Grade {grade}
                    </span>
                  ) : null}
                  {studentProfileStatus === "loading" && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 shrink-0">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                      Loading your profile…
                    </span>
                  )}
                  <select
                    value={dialectKey}
                    onChange={(e) => setDialectKey(e.target.value)}
                    disabled={
                      isConnected || busy || !profileReady || dialectOptions.length === 0
                    }
                    title={
                      dialectOptions.length === 0
                        ? "Loading school dialects…"
                        : "Select school dialect"
                    }
                    aria-busy={dialectOptions.length === 0}
                    className="text-xs px-2 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 shrink-0 outline-none cursor-pointer disabled:opacity-50 max-w-[min(100vw-8rem,14rem)]"
                  >
                    {dialectOptions.length === 0 ? (
                      <option value={dialectKey}>
                        {dialectKey} (loading school dialects…)
                      </option>
                    ) : (
                      dialectOptions.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.name}
                        </option>
                      ))
                    )}
                  </select>
                  {tutorType === "ai_tutor" && (!!effectiveLessonType || isBodyPartsLesson) && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 shrink-0">
                      <Tag size={12} />
                      {normalizeLessonType(isBodyPartsLesson ? "body_parts" : effectiveLessonType)}
                    </span>
                  )}
                </div>
                {tutorType === "ai_tutor" && lessonTitle && (
                  <p className="text-sm text-emerald-700 mt-1 font-semibold truncate">
                    {lessonTitle}
                  </p>
                )}
                {tutorType === "ai_tutor" && selectedSublessonMeta && (
                  <p className="text-xs text-slate-600 mt-0.5 font-medium truncate flex flex-wrap items-center gap-2">
                    <span>{selectedSublessonMeta.title}</span>
                    {selectedSublessonMeta.type ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 border border-slate-200 shrink-0">
                        {selectedSublessonMeta.type}
                      </span>
                    ) : null}
                  </p>
                )}
                {tutorType === "communication_tutor" &&
                  (selectedTopic?.title || lessonTitle || initialTopicLabel) && (
                  <p className="text-sm text-indigo-700 mt-1 font-semibold truncate">
                    {selectedTopic?.title || lessonTitle || initialTopicLabel}
                  </p>
                )}
                {!!lessonStyle && (
                  <p className="text-[11px] text-slate-500 mt-1 truncate">
                    Style: {lessonStyle}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end md:justify-between gap-2 sm:gap-3 shrink-0">
                {tutorType === "ai_tutor" &&
                  currentLesson &&
                  hasSublessons(currentLesson) &&
                  selectedSublessonNo &&
                  sublessonNavList.length > 0 && (
                    <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-0.5">
                      <button
                        type="button"
                        title="Previous sublesson"
                        onClick={() => void stepSublesson(-1)}
                        disabled={
                          busy || isConnected || !profileReady || sublessonNavIndex <= 0
                        }
                        className="p-2 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed text-slate-700"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        type="button"
                        title="Next sublesson"
                        onClick={() => void stepSublesson(1)}
                        disabled={
                          busy ||
                          isConnected ||
                          !profileReady ||
                          sublessonNavIndex < 0 ||
                          sublessonNavIndex >= sublessonNavList.length - 1
                        }
                        className="p-2 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed text-slate-700"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  )}
                {showStoryStudioButton && (
                    <button
                      type="button"
                      onClick={() => {
                        const p = new URLSearchParams();
                        p.set("lesson", String(lessonNo));
                        if (grade != null) p.set("grade", String(grade));
                        if (selectedSublessonNo) p.set("sublesson", selectedSublessonNo);
                        const bid = currentLesson?.book_id;
                        if (typeof bid === "string" && bid.trim() !== "") p.set("book_id", bid.trim());
                        if (storyStudioSegmentParam) p.set("segment", storyStudioSegmentParam);
                        navigate(`${ROUTES.STUDENT_STORY_STUDIO}?${p.toString()}`);
                      }}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-100 border border-amber-300 hover:bg-amber-200 transition text-amber-800 font-semibold"
                    >
                      <Film size={18} />
                      <span className="text-xs">{storyStudioWatchLabel}</span>
                    </button>
                  )}
                {showToc && (
                  <button
                    type="button"
                    onClick={() => setTocOpen(true)}
                    className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 border border-slate-200 hover:bg-slate-200 transition"
                  >
                    <BookOpen size={18} />
                    <span className="text-xs font-semibold">Lessons</span>
                  </button>
                )}
                {showTopics && (
                  <button
                    type="button"
                    onClick={() => setTopicsDrawerOpen(true)}
                    className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-100 border border-indigo-200 hover:bg-indigo-200 transition"
                  >
                    <MessageCircle size={18} />
                    <span className="text-xs font-semibold">Topics</span>
                  </button>
                )}
                <button
                  onClick={async () => {
                    setError(null);
                    await disconnectSession();
                    setConnState("ready");
                  }}
                  disabled={busy}
                  className="p-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition disabled:opacity-50"
                  title="Reset"
                >
                  <RefreshCcw size={18} />
                </button>
                <div className="text-indigo-700 font-mono font-extrabold text-base sm:text-lg w-14 sm:w-16 text-right">
                  {timeLeft}s
                </div>
              </div>
            </div>

            {/* STATUS */}
            <div className="shrink-0 px-4 sm:px-6 py-2 sm:py-3 bg-white border-b border-slate-200">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-extrabold text-slate-500">
                <span
                  className={`w-2 h-2 rounded-full ${
                    isConnected ? "bg-emerald-500 animate-pulse" : "bg-slate-300"
                  }`}
                />
                LIVE:{livePhase.toUpperCase()} · {liveStatusConnState.toUpperCase()}
              </div>
              {studentProfileError && (
                <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {studentProfileError}
                </div>
              )}
              {error && !studentProfileError && !commTopicsStillLoading && (
                <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {error}
                </div>
              )}
            </div>

            {/* {tutorType === "communication_tutor" && profileReady && (
              <div className="shrink-0 px-4 sm:px-6 py-2 border-b border-slate-200 bg-white space-y-2">
                {lastTurnInsight && (
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 text-sm">
                    <p className="text-[10px] font-extrabold text-indigo-900 uppercase tracking-wide">
                      Skill feedback
                    </p>
                    <p className="mt-1 text-slate-800">
                      You practiced:{" "}
                      <span className="font-semibold text-indigo-900">
                        {lastTurnInsight.statements_for_ui[0]?.statement_en ||
                          lastTurnInsight.statements_for_ui[0]?.statement_ta ||
                          lastTurnInsight.can_do.auto_ids[0] ||
                          "—"}
                      </span>
                    </p>
                    <div className="mt-2 space-y-1.5">
                      {(
                        [
                          ["Fluency", lastTurnInsight.performance.fluency],
                          ["Accuracy", lastTurnInsight.performance.accuracy],
                          ["Appropriateness", lastTurnInsight.performance.appropriateness],
                        ] as const
                      ).map(([label, v]) => (
                        <div key={label}>
                          <div className="flex justify-between text-[10px] text-slate-600">
                            <span>{label}</span>
                            <span>{Math.round(v * 100)}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500 rounded-full transition-all"
                              style={{ width: `${Math.round(v * 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setSkillProgressOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left text-xs font-bold text-slate-800 hover:bg-slate-100/80"
                  >
                    <span>Skill progress</span>
                    <ChevronDown
                      size={16}
                      className={`text-slate-500 transition-transform ${skillProgressOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {skillProgressOpen && (
                    <div className="px-3 pb-3 text-xs border-t border-slate-200 bg-white">
                      {skillProgressLoading && (
                        <div className="py-3 flex items-center gap-2 text-slate-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading…
                        </div>
                      )}
                      {!skillProgressLoading && skillProgress && skillProgress.skills.length === 0 && (
                        <p className="py-2 text-slate-500">Practice more to see skill trends.</p>
                      )}
                      {!skillProgressLoading && skillProgress && skillProgress.skills.length > 0 && (
                        <div className="space-y-3 pt-2">
                          {(() => {
                            const mean = (p: {
                              fluency: number;
                              accuracy: number;
                              appropriateness: number;
                            }) => (p.fluency + p.accuracy + p.appropriateness) / 3;
                            const done = skillProgress.skills.filter((s) => mean(s.avg_performance) >= 0.7);
                            const mid = skillProgress.skills.filter((s) => {
                              const m = mean(s.avg_performance);
                              return m >= 0.4 && m < 0.7;
                            });
                            const low = skillProgress.skills.filter((s) => mean(s.avg_performance) < 0.4);
                            const block = (title: string, icon: string, items: typeof skillProgress.skills) =>
                              items.length ? (
                                <div>
                                  <p className="font-bold text-slate-700">
                                    {icon} {title}
                                  </p>
                                  <ul className="mt-1 space-y-1 text-slate-600">
                                    {items.map((s) => (
                                      <li key={s.can_do_id} className="line-clamp-2">
                                        {s.statement}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null;
                            return (
                              <>
                                {block("Completed", "✅", done)}
                                {block("Improving", "⚠️", mid)}
                                {block("Needs practice", "❌", low)}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )} */}

            {/* Teacher + optional body-part cards (this lesson type only) */}
            <div
              className={`flex-1 min-h-0 bg-slate-50 overflow-hidden ${
                tutorType === "communication_tutor"
                  ? "flex flex-col lg:flex-row items-stretch"
                  : `flex flex-col lg:flex-row ${isBodyPartsLesson ? "lg:gap-0" : ""}`
              }`}
            >
              <div
                className={`flex flex-col items-center justify-center min-h-0 overflow-y-auto overflow-x-hidden py-3 px-2 ${
                  isBodyPartsLesson
                    ? "lg:w-[44%] lg:min-w-[280px] lg:border-r lg:border-slate-200"
                    : tutorType === "communication_tutor"
                    ? "flex-1"
                    : "w-full"
                }`}
              >
                <div
                  className={`relative py-2 w-full flex flex-col items-center gap-3 ${
                    showGrade3TextbookPanel ? "lg:flex-row lg:items-start lg:justify-center" : ""
                  }`}
                >
                  <div className="relative py-2 flex flex-col items-center">
                    <div
                      className={`absolute rounded-full blur-2xl sm:blur-3xl transition-opacity duration-500 inset-[-24px] sm:inset-[-50px] ${
                        isTeacherSpeaking ? "bg-amber-300/40 opacity-100" : "opacity-0"
                      }`}
                    />
                    <div
                      className={`relative rounded-full overflow-hidden w-32 h-32 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 p-1 sm:p-2 border-4 sm:border-8 transition-colors duration-500 bg-white ${
                        isTeacherSpeaking ? "border-amber-400" : "border-slate-200"
                      }`}
                    >
                      <img
                        src="/images/tamil-teacher.png"
                        className="w-full h-full object-contain block"
                        alt="Teacher"
                      />
                    </div>

                    <div className="mt-2 sm:mt-4 text-center px-2">
                      <p className="text-xs sm:text-sm font-semibold text-slate-700">
                        {isConnected
                          ? isTeacherSpeaking
                            ? "Teacher speaking..."
                            : userIsSpeaking
                            ? "Listening..."
                            : "Connected"
                          : "Offline"}
                      </p>
                      {isBodyPartsLesson && (
                        <p className="mt-1 text-[11px] text-slate-500 max-w-xs mx-auto">
                          {isConnected
                            ? "Turn the mic on if you want to speak before the teacher greets you. After the greeting, tap a picture on the right."
                            : "Start class — turn the mic on yourself when you want to talk; the teacher greets after a short moment."}
                        </p>
                      )}
                    </div>
                  </div>
                  {showGrade3TextbookPanel ? (
                    <TutorGrade3TextbookPanel
                      assets={grade3TextbookAssets}
                      pageIndex={g3BookPageIx}
                      onPrev={() => setG3BookPageIx((i) => Math.max(0, i - 1))}
                      onNext={() =>
                        setG3BookPageIx((i) =>
                          Math.min(Math.max(0, grade3TextbookAssets.length - 1), i + 1)
                        )
                      }
                    />
                  ) : null}
                </div>
              </div>
              {/* Communication tutor: render progress panel next to teacher image */}
              {tutorType === "communication_tutor" && communicationProgressPanel && (
                <div className="w-full lg:w-[380px] xl:w-[420px] shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-white/80 overflow-y-auto p-4">
                  {communicationProgressPanel}
                </div>
              )}

              {isBodyPartsLesson && (
                <div className="flex-1 min-h-0 flex flex-col min-w-0 border-t lg:border-t-0 border-slate-200 bg-white/60">
                  <div className="shrink-0 px-3 py-2 border-b border-slate-100 bg-indigo-50/50">
                    <p className="text-xs font-extrabold text-indigo-900">Body parts</p>
                    <p className="text-[10px] text-indigo-700/90">Choose a card to hear the name and a short lesson in Tamil.</p>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
                      {bodyPartsForCards.map((part) => (
                        <BodyPartCard
                          key={part.id}
                          part={part}
                          disabled={busy}
                          active={activeBodyPartId === part.id}
                          onSelect={() => void explainBodyPart(part)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CONTROLS (sticky bottom, safe-area; mobile: icon-only row, desktop: full buttons) */}
            <div className="sticky bottom-0 z-20 shrink-0 px-3 py-2 sm:px-6 sm:py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-white border-t border-slate-200 flex flex-row items-center justify-center gap-3 sm:gap-4">
              {!isConnected ? (
                <button
                  type="button"
                  onClick={connectSession}
                  aria-busy={isStarting}
                  disabled={
                    !canConnect ||
                    busy ||
                    !systemInstruction ||
                    (showTopics && topics.length > 0 && !selectedTopic)
                  }
                  title={
                    showTopics && topics.length > 0 && !selectedTopic
                      ? "Choose what to practice first"
                      : isStarting
                        ? "Starting…"
                        : "Start Class"
                  }
                  className="h-14 w-14 sm:h-auto sm:w-auto sm:px-6 sm:py-2.5 rounded-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-200 disabled:text-slate-500 transition flex items-center justify-center gap-3 font-extrabold text-white shadow-sm shrink-0"
                >
                  {isStarting ? (
                    <Loader2
                      className="w-[22px] h-[22px] sm:w-5 sm:h-5 shrink-0 animate-spin"
                      aria-hidden
                    />
                  ) : (
                    <PlugZap size={22} aria-hidden />
                  )}
                  <span className="hidden sm:inline" aria-live="polite">
                    {isStarting ? "Starting..." : "Start Class"}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => void disconnectSession()}
                  title="Disconnect"
                  className="h-14 w-14 sm:h-auto sm:w-auto sm:px-6 sm:py-2.5 rounded-full bg-white hover:bg-slate-50 transition flex items-center justify-center gap-3 font-extrabold border border-slate-200 shrink-0"
                >
                  <Unplug size={22} />
                  <span className="hidden sm:inline">Disconnect</span>
                </button>
              )}

              <button
                onClick={() => setIsMuted((v) => !v)}
                disabled={!isConnected}
                title={isMuted ? "Unmute" : "Mute"}
                className={`relative h-14 w-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm border-4 shrink-0 ${
                  !isConnected
                    ? "bg-slate-100 border-slate-200 opacity-50"
                    : isMuted
                    ? "bg-white border-red-300 text-red-600 hover:bg-red-50"
                    : "bg-emerald-600 border-emerald-500 text-white hover:scale-105"
                }`}
              >
                {isMuted ? <MicOff size={28} /> : <Mic size={28} />}
              </button>

              <button
                onClick={async () => {
                  await disconnectSession();
                  onEnd();
                }}
                title="End Class"
                className="h-14 w-14 sm:h-auto sm:w-auto sm:px-6 sm:py-2.5 rounded-full bg-white hover:bg-red-50 text-red-600 transition flex items-center justify-center gap-2 font-extrabold border border-red-200 shrink-0"
              >
                <PhoneOff size={22} />
                <span className="hidden sm:inline">End Class</span>
              </button>
            </div>
          </div>

          {/* ✅ END-OF-CLASS POPUP */}
          {showEndPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-xl w-[90%] max-w-md p-6 text-center">
                <h2 className="text-xl font-extrabold text-emerald-700">
                  📘 இன்றைய வகுப்பு முடிந்தது
                </h2>

                <p className="mt-3 text-sm text-slate-700">
                  இன்று நீங்கள் சிறப்பாக பயின்றீர்கள் 😊 நாளை மீண்டும் சந்திப்போம்!
                </p>

                <button
                  onClick={() => {
                    setShowEndPopup(false);
                    if (timerRef.current) {
                      window.clearInterval(timerRef.current);
                      timerRef.current = null;
                    }
                    hasTimerStartedRef.current = false;
                    setTimeLeft(TOTAL_TIME);
                    if (sessionPromiseRef.current != null) {
                      void disconnectSession();
                    }
                  }}
                  className="mt-6 px-6 py-3 rounded-full bg-indigo-600 text-white font-extrabold"
                >
                  சரி
                </button>
              </div>
            </div>
          )}

          {/* TOC — desktop sidebar */}
          {showToc && (
            <aside className="hidden lg:flex col-span-3 h-full min-h-0 border-l border-slate-200 bg-white overflow-hidden flex flex-col">
              {tocContent}
            </aside>
          )}

          {/* Topics — desktop sidebar */}
          {showTopics && (
            <aside className="hidden lg:flex col-span-3 h-full min-h-0 border-l border-slate-200 bg-white overflow-hidden flex flex-col">
              {topicsContent}
            </aside>
          )}

          {/* Mobile TOC drawer */}
          {showToc && tocOpen && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/40 lg:hidden"
                onClick={() => setTocOpen(false)}
                aria-hidden
              />
              <aside
                className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-white shadow-xl flex flex-col lg:hidden"
                aria-modal
                aria-label="Lessons"
              >
                {tocContent}
              </aside>
            </>
          )}

          {/* Mobile topics drawer */}
          {showTopics && topicsDrawerOpen && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/40 lg:hidden"
                onClick={() => setTopicsDrawerOpen(false)}
                aria-hidden
              />
              <aside
                className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-white shadow-xl flex flex-col lg:hidden"
                aria-modal
                aria-label="Topics"
              >
                {topicsContent}
              </aside>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}